import React, { useRef, useEffect, useMemo, useCallback, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { BarChart3, GitBranch, Settings, Download } from 'lucide-react';
import * as d3 from 'd3';
import { sankey, sankeyLinkHorizontal } from 'd3-sankey';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import useScrollIndicator from '../hooks/useScrollIndicator';
import ScrollIndicator from './ScrollIndicator';

// Debounce function for performance
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const SankeyDiagram = ({ lcaData, selectedImpact = 'gwp' }) => {
  const svgRef = useRef(null);
  const renderTimeoutRef = useRef(null);
  const {
    containerRef: sankeyScrollContainerRef,
    indicator: sankeyScrollIndicator,
    update: updateSankeyScrollIndicator,
  } = useScrollIndicator();

      // State for managing visible columns
    const [visibleColumns, setVisibleColumns] = useState({
      building: true,
      categories: true,
      components: true,
      materials: true,
      lcaStages: true
    });
    
      // State for aggregating results (only for Excel upload)
  const [aggregateResults, setAggregateResults] = useState(false);
  
  // State for PDF download
  const [isDownloading, setIsDownloading] = useState(false);

    // Update visible columns when data changes
    useEffect(() => {
      // Check if it's sample data (mock data has a specific structure)
      // More specific detection: only detect as sample data if explicitly marked OR if it's the exact mock data structure
      const isSampleData = lcaData?.isSampleData === true || 
        (lcaData?.components && lcaData.components.length === 12 && 
         lcaData.components[0]?.category === 'Structure' && 
         lcaData.components[0]?.name === 'Concrete Foundation' && 
         lcaData.components[1]?.category === 'Envelope' && 
         lcaData.components[1]?.name === 'Exterior Walls' &&
         lcaData.components[2]?.category === 'MEP' && 
         lcaData.components[2]?.name === 'HVAC System'); // More specific mock data detection
      
      // Check if actual material data exists in the Excel file
      const hasActualMaterialData = isSampleData ? true : (lcaData?.components?.some(component => 
        component.materials && Array.isArray(component.materials) && component.materials.length > 0
      ) || false);

      // Check if actual LCA stage data exists from second sheet
      const hasActualLCAStageData = isSampleData ? true : (lcaData?.lcaStagesData && 
        Object.keys(lcaData.lcaStagesData || {}).length > 0);

      // Debug logging to see what's happening
      console.log('🔍 Excel Upload Detection:', {
        isSampleData,
        hasActualMaterialData,
        hasActualLCAStageData,
        componentCount: lcaData?.components?.length,
        hasComponents: !!lcaData?.components,
        hasLCAStages: !!lcaData?.lcaStagesData
      });

      // Smart default logic: Categories + LCA Stages, but fallback to all available data if LCA stages missing
      if (isSampleData) {
        // Sample data: always Categories + LCA Stages
        console.log('📊 Sample data: Categories + LCA Stages only');
        setVisibleColumns({
          building: false,
          categories: true,
          components: false,
          materials: false,
          lcaStages: true
        });
      } else {
        // Excel upload: check if LCA stages data exists
        if (hasActualLCAStageData) {
          // If LCA stages exist: Categories + LCA Stages only
          console.log('📊 Excel upload with LCA stages: Categories + LCA Stages only');
          setVisibleColumns({
            building: false,
            categories: true,
            components: false,
            materials: false,
            lcaStages: true
          });
        } else {
          // If LCA stages missing: check all available data columns
          console.log('📊 Excel upload without LCA stages: All available columns checked');
          setVisibleColumns({
            building: true,
            categories: true,
            components: true,
            materials: hasActualMaterialData,
            lcaStages: false
          });
        }
      }
    }, [lcaData]);

  // Warm and cool color palette - yellow, orange, navy blue, teal, green
  const categoryColors = {
    'Structure': '#0D9488',      // Teal
    'Envelope': '#F59E0B',       // Yellow
    'MEP': '#1E3A8A',            // Navy Blue
    'Finishes': '#EA580C',       // Orange
    'Foundation': '#1E40AF',     // Dark Blue
    'Roofing': '#0891B2',        // Teal Blue
    'Electrical': '#FCD34D',     // Light Yellow
    'HVAC': '#FB923C',           // Light Orange
    'Plumbing': '#0EA5E9',       // Sky Blue
    'Interior': '#16A34A',       // Green
    'Exterior': '#F97316',       // Orange
    'Landscaping': '#22C55E',    // Green
    'Other': '#6B7280'           // Gray
  };

  // Building color
  const buildingColor = '#2C3E50';

  // Improved material detection with more comprehensive patterns
  const getMaterialFromComponent = useCallback((componentName) => {
    const name = componentName.toLowerCase();
    
    // Structure materials
    if (name.includes('concrete') || name.includes('cement') || name.includes('foundation') || name.includes('slab')) return 'Concrete';
    if (name.includes('steel') || name.includes('metal') || name.includes('beam') || name.includes('column') || name.includes('frame')) return 'Steel';
    if (name.includes('wood') || name.includes('timber') || name.includes('lumber') || name.includes('plywood')) return 'Wood';
    if (name.includes('brick') || name.includes('masonry') || name.includes('block') || name.includes('stone')) return 'Brick/Masonry';
    
    // Envelope materials
    if (name.includes('glass') || name.includes('window') || name.includes('glazing')) return 'Glass';
    if (name.includes('insulation') || name.includes('foam') || name.includes('fiberglass') || name.includes('mineral wool')) return 'Insulation';
    if (name.includes('roof') || name.includes('shingle') || name.includes('tile')) return 'Roofing';
    if (name.includes('wall') || name.includes('cladding') || name.includes('siding')) return 'Wall Materials';
    
    // Finishes
    if (name.includes('paint') || name.includes('coating') || name.includes('finish')) return 'Paint/Coating';
    if (name.includes('floor') || name.includes('carpet') || name.includes('tile')) return 'Flooring';
    if (name.includes('ceiling') || name.includes('acoustic')) return 'Ceiling';
    
    // MEP materials
    if (name.includes('plastic') || name.includes('pvc') || name.includes('pipe')) return 'Plastic/PVC';
    if (name.includes('aluminum') || name.includes('aluminium') || name.includes('duct')) return 'Aluminum';
    if (name.includes('copper') || name.includes('electrical') || name.includes('wire')) return 'Copper/Electrical';
    
    // Default fallback
    return 'Other Materials';
  }, []);

  // Memoized stage distribution function
  const getStageDistribution = useCallback((materialName) => {
    const material = materialName.toLowerCase();
    
    if (material.includes('concrete') || material.includes('cement')) {
      return { extraction: 0.15, manufacturing: 0.70, transport: 0.05, construction: 0.05, use: 0.03, end_of_life: 0.02 };
    } else if (material.includes('steel') || material.includes('metal')) {
      return { extraction: 0.25, manufacturing: 0.60, transport: 0.08, construction: 0.03, use: 0.02, end_of_life: 0.02 };
    } else if (material.includes('wood') || material.includes('timber')) {
      return { extraction: 0.10, manufacturing: 0.40, transport: 0.15, construction: 0.20, use: 0.10, end_of_life: 0.05 };
    } else if (material.includes('glass')) {
      return { extraction: 0.20, manufacturing: 0.65, transport: 0.10, construction: 0.02, use: 0.02, end_of_life: 0.01 };
    } else if (material.includes('plastic') || material.includes('pvc')) {
      return { extraction: 0.30, manufacturing: 0.55, transport: 0.08, construction: 0.03, use: 0.02, end_of_life: 0.02 };
    } else {
      // Default distribution for other materials
      return { extraction: 0.20, manufacturing: 0.60, transport: 0.10, construction: 0.05, use: 0.03, end_of_life: 0.02 };
    }
  }, []);

  // Helper function to get LCA stage description
  const getStageDescription = useCallback((stageName) => {
    const descriptions = {
      'Raw Material Extraction': 'Resource mining and harvesting',
      'Manufacturing': 'Production and processing',
      'Transport': 'Material delivery and logistics',
      'Construction': 'On-site assembly and installation',
      'Use Phase': 'Operational maintenance and energy',
      'End of Life': 'Demolition and waste management'
    };
    return descriptions[stageName] || 'Life cycle phase';
  }, []);

  // Function to download the Sankey diagram as PDF
  const downloadDiagramAsPDF = async () => {
    if (isDownloading) return; // Prevent multiple downloads
    
    setIsDownloading(true);
    try {
      console.log('Starting Sankey diagram PDF generation...');
      
      // Import jsPDF dynamically to avoid SSR issues
      const { jsPDF } = await import('jspdf');
      
      console.log('jsPDF imported successfully');
      
      // Method 1: Try to get SVG element directly from svgRef
      let svgElement = svgRef.current;
      if (!svgElement) {
        throw new Error('Sankey diagram container not found');
      }
      
      console.log('SVG container found:', svgElement);
      console.log('SVG container tagName:', svgElement.tagName);
      console.log('SVG container innerHTML length:', svgElement.innerHTML?.length || 0);
      
      // Wait for any animations to complete and ensure diagram is fully rendered
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Additional wait to ensure D3 simulation is complete
      if (svgRef.current && svgRef.current.querySelector('svg')) {
        console.log('Waiting for D3 simulation to complete...');
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      // Try multiple methods to get the SVG content
      let svgData = null;
      let method = 'unknown';
      
      // Method 1: Direct SVG serialization
      try {
        svgData = new XMLSerializer().serializeToString(svgElement);
        method = 'direct';
        console.log('Method 1 (direct) successful, SVG length:', svgData.length);
      } catch (error) {
        console.log('Method 1 failed, trying method 2...');
        
        // Method 2: Find SVG within the container
        const innerSVG = svgElement.querySelector('svg');
        if (innerSVG) {
          try {
            svgData = new XMLSerializer().serializeToString(innerSVG);
            method = 'inner';
            console.log('Method 2 (inner) successful, SVG length:', svgData.length);
          } catch (innerError) {
            console.log('Method 2 (inner) serialization failed, trying method 3...');
            
            // Method 2b: Try to get SVG as data URL
            try {
              const svgString = innerSVG.outerHTML;
              const blob = new Blob([svgString], { type: 'image/svg+xml' });
              svgData = URL.createObjectURL(blob);
              method = 'inner-blob';
              console.log('Method 2b (inner-blob) successful');
            } catch (blobError) {
              console.log('Method 2b failed, trying method 3...');
            }
          }
        } else {
          console.log('Method 2 failed, trying method 3...');
          
          // Method 3: Direct canvas creation without html2canvas (most reliable)
          try {
            const innerSVG = svgElement.querySelector('svg');
            if (innerSVG) {
              console.log('Method 3: Creating direct canvas representation...');
              
              // Create a high-resolution canvas for crisp PDF export
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              // Use 4x resolution for maximum clarity and crisp PDF export
              const scale = 4;
              canvas.width = 1200 * scale;
              canvas.height = 800 * scale;
              
              // Scale the context to maintain proper proportions
              ctx.scale(scale, scale);
              
              // Enable maximum quality rendering
              ctx.imageSmoothingEnabled = true;
              ctx.imageSmoothingQuality = 'high';
              
              // Fill with background
              ctx.fillStyle = '#f9fafb';
              ctx.fillRect(0, 0, canvas.width, canvas.height);
              
              // Draw simplified flow representation
              const centerY = canvas.height / 2;
              const startX = 150;
              const endX = canvas.width - 150;
              const boxWidth = 120;
              const boxHeight = 40;
              
              // Draw vertical columns (simplified representation)
              const columns = ['Building', 'Categories', 'Components', 'Materials', 'LCA Stages'];
              const columnWidth = (endX - startX) / (columns.length - 1);
              
              columns.forEach((col, index) => {
                const x = startX + (index * columnWidth) - (boxWidth / 2);
                const y = centerY - (boxHeight / 2);
                
                // Draw box
                ctx.fillStyle = '#e5e7eb';
                ctx.fillRect(x, y, boxWidth, boxHeight);
                ctx.strokeStyle = '#9ca3af';
                ctx.lineWidth = 2;
                ctx.strokeRect(x, y, boxWidth, boxHeight);
                
                // Draw text
                ctx.fillStyle = '#374151';
                ctx.font = '12px Arial';
                ctx.textAlign = 'center';
                ctx.fillText(col, x + (boxWidth / 2), y + (boxHeight / 2) + 4);
                
                // Draw arrow to next column
                if (index < columns.length - 1) {
                  const arrowStartX = x + boxWidth + 5;
                  const arrowEndX = startX + ((index + 1) * columnWidth) - (boxWidth / 2) - 5;
                  const arrowY = centerY;
                  
                  ctx.strokeStyle = '#10b981';
                  ctx.lineWidth = 3;
                  ctx.beginPath();
                  ctx.moveTo(arrowStartX, arrowY);
                  ctx.lineTo(arrowEndX, arrowY);
                  ctx.stroke();
                  
                  // Arrow head
                  ctx.beginPath();
                  ctx.moveTo(arrowEndX - 10, arrowY - 5);
                  ctx.lineTo(arrowEndX, arrowY);
                  ctx.lineTo(arrowEndX - 10, arrowY + 5);
                  ctx.stroke();
                }
              });
              

              
              // Use JPEG with 100% quality for crisp PDF export with no compression
              svgData = canvas.toDataURL('image/jpeg', 1.0);
              method = 'direct-canvas';
              console.log('Method 3 (direct canvas) successful');
            } else {
              throw new Error('No SVG element found');
            }
          } catch (directCanvasError) {
            console.error('Direct canvas method failed:', directCanvasError);
            throw new Error('Unable to create diagram representation. Please ensure the diagram is fully loaded.');
          }
        }
      }
      
      if (!svgData) {
        throw new Error('No diagram content could be captured');
      }
      
      console.log(`Using method: ${method}`);
      
      // Create PDF in landscape mode for better fit
      const pdf = new jsPDF('landscape', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      console.log('PDF created, page dimensions:', { pageWidth, pageHeight });
      
      // Calculate dimensions to fit the diagram properly (larger for maximum clarity)
      const imgWidth = Math.min(280, pageWidth - 15); // mm - minimal margins for maximum size
      const imgHeight = Math.min(200, pageHeight - 15); // mm - use more page space
      const x = (pageWidth - imgWidth) / 2;
      const y = (pageHeight - imgHeight) / 2;
      
      // Convert all data to PNG format for consistent PDF generation
      let finalImageData = svgData;
      
      if (method === 'direct-canvas') {
        // Data is already a PNG data URL from canvas
        console.log('Using direct canvas PNG data');
        finalImageData = svgData;
      } else if (method === 'inner-blob') {
        // Data is a blob URL, need to convert to PNG
        console.log('Converting blob URL to PNG...');
        try {
          const img = new Image();
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = svgData;
          });
          
          // Create high-resolution canvas from the loaded image
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          // Use 4x resolution for maximum clarity and crisp PDF export
          const scale = 4;
          const baseWidth = img.width || 1200;
          const baseHeight = img.height || 800;
          canvas.width = baseWidth * scale;
          canvas.height = baseHeight * scale;
          
          // Scale the context to maintain proper proportions
          ctx.scale(scale, scale);
          
          // Enable maximum quality rendering
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          
          // Draw image to canvas
          ctx.fillStyle = '#f9fafb';
          ctx.fillRect(0, 0, baseWidth, baseHeight);
          ctx.drawImage(img, 0, 0, baseWidth, baseHeight);
          
          finalImageData = canvas.toDataURL('image/jpeg', 1.0);
          console.log('Blob converted to JPEG successfully');
          
          // Clean up blob URL
          URL.revokeObjectURL(svgData);
        } catch (conversionError) {
          console.error('Blob conversion failed:', conversionError);
          throw new Error('Failed to convert image data for PDF');
        }
      } else {
        // For other SVG methods, convert to PNG via canvas
        console.log('Converting SVG data to PNG...');
        try {
          // Create a blob from SVG data
          const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
          const svgUrl = URL.createObjectURL(svgBlob);
          
          // Load SVG as image
          const img = new Image();
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = svgUrl;
          });
          
          // Convert to PNG via high-resolution canvas
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          // Use 4x resolution for maximum clarity and crisp PDF export
          const scale = 4;
          canvas.width = 1200 * scale;
          canvas.height = 800 * scale;
          
          // Scale the context to maintain proper proportions
          ctx.scale(scale, scale);
          
          // Enable maximum quality rendering
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          
          // Draw white background
          ctx.fillStyle = '#f9fafb';
          ctx.fillRect(0, 0, canvas.width / scale, canvas.height / scale);
          
          // Draw the SVG image
          ctx.drawImage(img, 0, 0, canvas.width / scale, canvas.height / scale);
          
          finalImageData = canvas.toDataURL('image/jpeg', 1.0);
          console.log('SVG converted to JPEG successfully');
          
          // Clean up
          URL.revokeObjectURL(svgUrl);
        } catch (svgConversionError) {
          console.error('SVG conversion failed:', svgConversionError);
          throw new Error('Failed to convert SVG to PNG for PDF');
        }
      }
      
      // Validate the image data
      if (!finalImageData || (!finalImageData.startsWith('data:image/png;base64,') && !finalImageData.startsWith('data:image/jpeg;base64,'))) {
        throw new Error('Invalid image data generated');
      }
      
      console.log('Final image data validated, length:', finalImageData.length);
      
      // Add the image to PDF (centered and full size)
      const imageFormat = finalImageData.startsWith('data:image/jpeg') ? 'JPEG' : 'PNG';
      pdf.addImage(finalImageData, imageFormat, x, y, imgWidth, imgHeight);
      console.log(`${imageFormat} image added to PDF at position:`, { x, y, width: imgWidth, height: imgHeight });
      
      // Download the PDF
      const filename = `Sankey_Diagram_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(filename);
      console.log('PDF saved successfully as:', filename);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      const errorMessage = error?.message || error?.toString() || 'Unknown error occurred';
      alert(`Failed to generate PDF: ${errorMessage}. Please try again.`);
    } finally {
      setIsDownloading(false);
    }
  };

  // Check if it's sample data (mock data has a specific structure)
  // More specific detection: only detect as sample data if explicitly marked OR if it's the exact mock data structure
  const isSampleData = lcaData?.isSampleData === true || 
    (lcaData?.components && lcaData.components.length > 0 && 
     lcaData.components[0]?.category === 'Structure' && 
     lcaData.components[0]?.name === 'Concrete Foundation' && 
     lcaData.components.length === 12); // Mock data has exactly 12 components
  


  // Check if actual material data exists in the Excel file
  const hasActualMaterialData = isSampleData ? true : (lcaData?.components?.some(component => 
    component.materials && Array.isArray(component.materials) && component.materials.length > 0
  ) || false);

  // Check if actual LCA stage data exists from second sheet
  const hasActualLCAStageData = isSampleData ? true : (lcaData?.lcaStagesData && 
    Object.keys(lcaData.lcaStagesData || {}).length > 0);

  // Transform LCA data into Sankey format with performance optimizations
  const sankeyData = useMemo(() => {
    if (!lcaData) return { nodes: [], links: [] };
    

    console.log('Sankey diagram received lcaData:', lcaData);
    console.log('Building info:', lcaData.buildingInfo);
    console.log('Components:', lcaData.components);
    console.log('LCA stages data:', lcaData.lcaStagesData);
    
    // Ensure lcaData has required properties
    if (!lcaData.components || !Array.isArray(lcaData.components)) {
      console.warn('Invalid LCA data: missing or invalid components array');
      return { nodes: [], links: [] };
    }
    
    // Validate each component has required properties
    const validComponents = lcaData.components.filter(component => {
      if (!component.id || !component.name || !component.category) {
        console.warn('Invalid component found:', component);
        return false;
      }
      if (!component.impacts || typeof component.impacts !== 'object') {
        console.warn('Component missing impacts:', component);
        return false;
      }
      return true;
    });
    
    if (validComponents.length === 0) {
      console.warn('No valid components found after validation');
      return { nodes: [], links: [] };
    }
    
    // Replace components with validated ones
    lcaData.components = validComponents;
    console.log('Validated components:', lcaData.components);
    
    // Ensure buildingInfo exists and has all required properties
    if (!lcaData.buildingInfo) {
      console.warn('Building info missing, using defaults');
      lcaData.buildingInfo = {
        name: 'Building',
        area: 1000,
        location: 'Unknown',
        serviceLife: 50
      };
    } else {
      // Validate each property of buildingInfo
      if (!lcaData.buildingInfo.name || typeof lcaData.buildingInfo.name !== 'string') {
        lcaData.buildingInfo.name = 'Building';
      }
      if (!lcaData.buildingInfo.area || isNaN(lcaData.buildingInfo.area)) {
        lcaData.buildingInfo.area = 1000;
      }
      if (!lcaData.buildingInfo.location || typeof lcaData.buildingInfo.location !== 'string') {
        lcaData.buildingInfo.location = 'Unknown';
      }
      if (!lcaData.buildingInfo.serviceLife || isNaN(lcaData.buildingInfo.serviceLife)) {
        lcaData.buildingInfo.serviceLife = 50;
      }
    }
    
    console.log('Validated building info:', lcaData.buildingInfo);

    const nodes = [];
    const links = [];
    const nodeMap = new Map();

    // Add building info node (only if visible)
    if (visibleColumns.building) {
      // Ensure buildingInfo exists and has required properties
      const buildingInfo = lcaData.buildingInfo || {
        name: 'Building',
        area: 1000,
        location: 'Unknown',
        serviceLife: 50
      };
      
    const buildingNode = {
      id: 'building',
        name: buildingInfo.name || 'Building',
      type: 'building',
      group: 'building'
    };
    nodes.push(buildingNode);
    nodeMap.set('building', buildingNode);
    }

    // Add category nodes (only if visible)
    if (visibleColumns.categories) {
    const categories = [...new Set(lcaData.components.map(c => c.category))];
    categories.forEach(category => {
      const categoryNode = {
        id: `category_${category}`,
        name: category,
        type: 'category',
        group: category
      };
      nodes.push(categoryNode);
      nodeMap.set(`category_${category}`, categoryNode);
    });
    }

        // Add component nodes (only if visible)
    if (visibleColumns.components) {
    lcaData.components.forEach(component => {
        // Ensure impact value is valid
        const impactValue = component.impacts[selectedImpact];
        if (impactValue === undefined || impactValue === null || isNaN(impactValue)) {
          console.warn(`Invalid impact value for component ${component.name}:`, impactValue);
          return; // Skip this component
        }
        
      const componentNode = {
        id: `component_${component.id}`,
        name: component.name,
        type: 'component',
        group: component.category,
          impact: impactValue
      };
      nodes.push(componentNode);
      nodeMap.set(`component_${component.id}`, componentNode);
    });
    }

    // Add material nodes if materials column is visible
    if (visibleColumns.materials && hasActualMaterialData) {
      if (isSampleData) {
        // For sample data, create sample materials
        const sampleMaterials = [
          'Concrete', 'Steel', 'Glass', 'Wood', 'Insulation', 'Paint', 'Ceramic', 'Plastic'
        ];
        
        sampleMaterials.forEach(materialName => {
      const materialNode = {
            id: `material_${materialName.replace(/[^a-zA-Z0-9]/g, '_')}`,
            name: materialName,
        type: 'material',
            group: materialName
      };
      nodes.push(materialNode);
          nodeMap.set(`material_${materialName.replace(/[^a-zA-Z0-9]/g, '_')}`, materialNode);
        });
      } else {
        // Use actual material data from Excel
        const actualMaterials = new Set();
        lcaData.components.forEach(component => {
          if (component.materials && Array.isArray(component.materials)) {
            component.materials.forEach(material => {
              if (material.name) {
                actualMaterials.add(material.name);
              }
            });
          }
        });

        actualMaterials.forEach(materialName => {
          const materialNode = {
            id: `material_${materialName.replace(/[^a-zA-Z0-9]/g, '_')}`,
            name: materialName,
            type: 'material',
            group: materialName
          };
          nodes.push(materialNode);
          nodeMap.set(`material_${materialName.replace(/[^a-zA-Z0-9]/g, '_')}`, materialNode);
        });
      }
    }

    // Add LCA stage nodes if visible (either actual data exists OR it's sample data)
    if (visibleColumns.lcaStages && (hasActualLCAStageData || isSampleData)) {
      // Create grouped LCA stage nodes
      const stageGroups = [
        { id: 'A1-A3', name: 'A1-A3', description: 'Product Stage' },
        { id: 'A4-A5', name: 'A4-A5', description: 'Construction Process' },
        { id: 'B1-B7', name: 'B1-B7', description: 'Use Stage' },
        { id: 'C1-C4', name: 'C1-C4', description: 'End-of-Life' },
        { id: 'D', name: 'D', description: 'Benefits & Loads' }
      ];

      // For sample data, always show all stage groups
      // For uploaded Excel data, only show groups that have actual data
      if (isSampleData) {
        // Show all stage groups for sample data
        stageGroups.forEach(group => {
          const stageNode = {
            id: `stage_${group.id.replace(/[^a-zA-Z0-9]/g, '_')}`,
            name: group.name,
            type: 'lca_stage',
            group: group.id,
            description: group.description
          };
          nodes.push(stageNode);
          nodeMap.set(`stage_${group.id.replace(/[^a-zA-Z0-9]/g, '_')}`, stageNode);
        });
      } else {
        // Check which groups have actual data for uploaded Excel files
        const groupsWithData = new Set();
        Object.keys(lcaData.lcaStagesData || {}).forEach(componentId => {
          const componentStages = (lcaData.lcaStagesData || {})[componentId];
          
          stageGroups.forEach(group => {
            const stageCodes = group.id === 'A1-A3' ? ['A1', 'A2', 'A3'] :
                              group.id === 'A4-A5' ? ['A4', 'A5'] :
                              group.id === 'B1-B7' ? ['B1', 'B2', 'B3', 'B4', 'B5', 'B6', 'B7'] :
                              group.id === 'C1-C4' ? ['C1', 'C2', 'C3', 'C4'] :
                              ['D'];
            
            const hasData = componentStages && stageCodes.some(stageCode => {
              const stageValue = componentStages[stageCode];
              return stageValue !== undefined && stageValue !== null && !isNaN(stageValue); // Accept all values, including negatives
            });
            
            if (hasData) {
              groupsWithData.add(group.id);
            }
          });
        });

        // Create nodes for groups that have data
        stageGroups.forEach(group => {
          if (groupsWithData.has(group.id)) {
            const stageNode = {
              id: `stage_${group.id.replace(/[^a-zA-Z0-9]/g, '_')}`,
              name: group.name,
              type: 'lca_stage',
              group: group.id,
              description: group.description
            };
            nodes.push(stageNode);
            nodeMap.set(`stage_${group.id.replace(/[^a-zA-Z0-9]/g, '_')}`, stageNode);
          }
        });
      }
    }

    // Create links based on visible columns
    const categories = [...new Set(lcaData.components.map(c => c.category))];
    
    // Building -> Categories (if both are visible)
    // This shows the normal building → categories → components → materials flow
    if (visibleColumns.building && visibleColumns.categories) {
    categories.forEach(category => {
      const categoryComponents = lcaData.components.filter(c => c.category === category);
      const totalImpact = categoryComponents.reduce((sum, c) => sum + (c.impacts[selectedImpact] || 0), 0);
      
      if (totalImpact > 0) {
        links.push({
          source: nodeMap.get('building'),
          target: nodeMap.get(`category_${category}`),
          value: totalImpact,
          type: 'building-category'
        });
      }
    });
    }

        // Categories -> Components (if both are visible)
    if (visibleColumns.categories && visibleColumns.components) {
      lcaData.components.forEach(component => {
        const impact = component.impacts[selectedImpact];
        if (impact === undefined || impact === null || isNaN(impact) || impact <= 0) {
          console.warn(`Invalid impact value for component ${component.name}:`, impact);
          return; // Skip this component
        }
        
        const sourceNode = nodeMap.get(`category_${component.category}`);
        const targetNode = nodeMap.get(`component_${component.id}`);
        
        if (!sourceNode || !targetNode) {
          console.warn(`Missing nodes for component ${component.name}:`, { sourceNode, targetNode });
          return; // Skip this component
        }
        
        links.push({
          source: sourceNode,
          target: targetNode,
          value: impact,
          type: 'category-component'
        });
      });
    }

    // Components -> Materials (if both are visible AND materials column is enabled)
    if (visibleColumns.components && visibleColumns.materials && hasActualMaterialData) {
    lcaData.components.forEach(component => {
      const impact = component.impacts[selectedImpact] || 0;
      if (impact > 0) {
          if (isSampleData) {
            // For sample data, create realistic material links based on actual component materials
            const componentMaterial = component.material; // Use the actual material from mock data
            
            // Create realistic material distribution based on component's actual material
            const materialDistribution = [];
            
            if (componentMaterial === 'Concrete') {
              materialDistribution.push({ material: 'Concrete', percentage: 0.85 }); // Primary material
              materialDistribution.push({ material: 'Steel', percentage: 0.15 }); // Reinforcement
            } else if (componentMaterial === 'Steel') {
              materialDistribution.push({ material: 'Steel', percentage: 0.90 }); // Primary material
              materialDistribution.push({ material: 'Paint', percentage: 0.10 }); // Protective coating
            } else if (componentMaterial === 'Brick') {
              materialDistribution.push({ material: 'Ceramic', percentage: 0.70 }); // Brick material
              materialDistribution.push({ material: 'Concrete', percentage: 0.25 }); // Mortar
              materialDistribution.push({ material: 'Paint', percentage: 0.05 }); // Coating
            } else if (componentMaterial === 'Glass') {
              materialDistribution.push({ material: 'Glass', percentage: 0.80 }); // Primary material
              materialDistribution.push({ material: 'Steel', percentage: 0.15 }); // Frame
              materialDistribution.push({ material: 'Plastic', percentage: 0.05 }); // Seals
            } else if (componentMaterial === 'Mineral Wool') {
              materialDistribution.push({ material: 'Insulation', percentage: 1.0 }); // Pure insulation
            } else if (componentMaterial === 'Copper') {
              materialDistribution.push({ material: 'Steel', percentage: 0.60 }); // Conduits and supports
              materialDistribution.push({ material: 'Plastic', percentage: 0.40 }); // Insulation and casings
            } else if (componentMaterial === 'Ceramic') {
              materialDistribution.push({ material: 'Ceramic', percentage: 0.95 }); // Primary material
              materialDistribution.push({ material: 'Concrete', percentage: 0.05 }); // Adhesive
            } else if (componentMaterial === 'Gypsum') {
              materialDistribution.push({ material: 'Paint', percentage: 0.60 }); // Gypsum-based material
              materialDistribution.push({ material: 'Steel', percentage: 0.30 }); // Metal studs
              materialDistribution.push({ material: 'Insulation', percentage: 0.10 }); // Insulation
            } else if (componentMaterial === 'Acoustic Panels') {
              materialDistribution.push({ material: 'Wood', percentage: 0.50 }); // Wood fiber
              materialDistribution.push({ material: 'Insulation', percentage: 0.30 }); // Acoustic material
              materialDistribution.push({ material: 'Steel', percentage: 0.20 }); // Mounting system
            } else if (componentMaterial === 'Low-VOC Paint') {
              materialDistribution.push({ material: 'Paint', percentage: 1.0 }); // Pure paint material
            } else {
              // Fallback for unknown materials
              materialDistribution.push({ material: 'Steel', percentage: 1.0 });
            }
            
            // Create links based on realistic material distribution
            materialDistribution.forEach(({ material: materialName, percentage }) => {
              const materialId = `material_${materialName.replace(/[^a-zA-Z0-9]/g, '_')}`;
              const materialNode = nodeMap.get(materialId);
              
              if (materialNode) {
                const materialValue = impact * percentage; // Realistic distribution of impact
                
        links.push({
                  source: nodeMap.get(`component_${component.id}`),
                  target: materialNode,
                  value: materialValue,
                  type: 'component-material'
                });
              }
            });
          } else if (component.materials && Array.isArray(component.materials)) {
            // Use actual material data from Excel
            component.materials.forEach(material => {
              if (material.name) {
                const materialId = `material_${material.name.replace(/[^a-zA-Z0-9]/g, '_')}`;
                const materialNode = nodeMap.get(materialId);
                
                if (materialNode) {
                  links.push({
                    source: nodeMap.get(`component_${component.id}`),
                    target: materialNode,
          value: impact,
                    type: 'component-material'
        });
                }
      }
    });
          }
        }
      });
    }

        // Materials -> LCA Stages (if both are visible AND either actual data exists OR it's sample data)
    if (visibleColumns.materials && visibleColumns.lcaStages && hasActualMaterialData && (hasActualLCAStageData || isSampleData)) {
    lcaData.components.forEach(component => {
      const impact = component.impacts[selectedImpact] || 0;
      if (impact > 0) {
          if (isSampleData) {
            // For sample data, we need to aggregate all component contributions to each material
            // Store the aggregated impacts for later processing
            if (!window.materialStageAggregator) {
              window.materialStageAggregator = new Map();
            }
            
            const componentMaterial = component.material;
            
            // Get the same material distribution as in component-to-material links
            let materialDistribution = [];
            
            if (componentMaterial === 'Concrete') {
              materialDistribution.push({ material: 'Concrete', percentage: 0.85 });
              materialDistribution.push({ material: 'Steel', percentage: 0.15 });
            } else if (componentMaterial === 'Steel') {
              materialDistribution.push({ material: 'Steel', percentage: 0.90 });
              materialDistribution.push({ material: 'Paint', percentage: 0.10 });
            } else if (componentMaterial === 'Brick') {
              materialDistribution.push({ material: 'Ceramic', percentage: 0.70 });
              materialDistribution.push({ material: 'Concrete', percentage: 0.25 });
              materialDistribution.push({ material: 'Paint', percentage: 0.05 });
            } else if (componentMaterial === 'Glass') {
              materialDistribution.push({ material: 'Glass', percentage: 0.80 });
              materialDistribution.push({ material: 'Steel', percentage: 0.15 });
              materialDistribution.push({ material: 'Plastic', percentage: 0.05 });
            } else if (componentMaterial === 'Mineral Wool') {
              materialDistribution.push({ material: 'Insulation', percentage: 1.0 });
            } else if (componentMaterial === 'Copper') {
              materialDistribution.push({ material: 'Steel', percentage: 0.60 });
              materialDistribution.push({ material: 'Plastic', percentage: 0.40 });
            } else if (componentMaterial === 'Ceramic') {
              materialDistribution.push({ material: 'Ceramic', percentage: 0.95 });
              materialDistribution.push({ material: 'Concrete', percentage: 0.05 });
            } else if (componentMaterial === 'Gypsum') {
              materialDistribution.push({ material: 'Paint', percentage: 0.60 });
              materialDistribution.push({ material: 'Steel', percentage: 0.30 });
              materialDistribution.push({ material: 'Insulation', percentage: 0.10 });
            } else if (componentMaterial === 'Acoustic Panels') {
              materialDistribution.push({ material: 'Wood', percentage: 0.50 });
              materialDistribution.push({ material: 'Insulation', percentage: 0.30 });
              materialDistribution.push({ material: 'Steel', percentage: 0.20 });
            } else if (componentMaterial === 'Low-VOC Paint') {
              materialDistribution.push({ material: 'Paint', percentage: 1.0 });
            } else {
              materialDistribution.push({ material: 'Steel', percentage: 1.0 });
            }
            
            // Aggregate impacts for each material and LCA stage
            materialDistribution.forEach(({ material: materialName, percentage }) => {
              const materialImpact = impact * percentage;
              
              // Create realistic LCA stage distribution based on material type
              let stageDistribution = {};
              if (materialName === 'Concrete') {
                stageDistribution = { 'A1-A3': 0.7, 'A4-A5': 0.15, 'B1-B7': 0.1, 'C1-C4': 0.04, 'D': 0.01 };
              } else if (materialName === 'Steel') {
                stageDistribution = { 'A1-A3': 0.6, 'A4-A5': 0.2, 'B1-B7': 0.15, 'C1-C4': 0.03, 'D': 0.02 };
              } else if (materialName === 'Glass') {
                stageDistribution = { 'A1-A3': 0.65, 'A4-A5': 0.18, 'B1-B7': 0.12, 'C1-C4': 0.03, 'D': 0.02 };
              } else if (materialName === 'Ceramic') {
                stageDistribution = { 'A1-A3': 0.75, 'A4-A5': 0.15, 'B1-B7': 0.08, 'C1-C4': 0.01, 'D': 0.01 };
              } else if (materialName === 'Wood') {
                stageDistribution = { 'A1-A3': 0.5, 'A4-A5': 0.2, 'B1-B7': 0.2, 'C1-C4': 0.08, 'D': 0.02 };
              } else if (materialName === 'Insulation') {
                stageDistribution = { 'A1-A3': 0.6, 'A4-A5': 0.15, 'B1-B7': 0.2, 'C1-C4': 0.03, 'D': 0.02 };
              } else if (materialName === 'Paint') {
                stageDistribution = { 'A1-A3': 0.7, 'A4-A5': 0.15, 'B1-B7': 0.1, 'C1-C4': 0.03, 'D': 0.02 };
              } else if (materialName === 'Plastic') {
                stageDistribution = { 'A1-A3': 0.65, 'A4-A5': 0.15, 'B1-B7': 0.15, 'C1-C4': 0.03, 'D': 0.02 };
              } else {
                stageDistribution = { 'A1-A3': 0.4, 'A4-A5': 0.2, 'B1-B7': 0.25, 'C1-C4': 0.1, 'D': 0.05 };
              }
              
              // Aggregate the impacts for each stage
              Object.entries(stageDistribution).forEach(([groupName, stagePercentage]) => {
                const key = `${materialName}_${groupName}`;
                if (!window.materialStageAggregator.has(key)) {
                  window.materialStageAggregator.set(key, {
                    material: materialName,
                    stage: groupName,
                    totalImpact: 0
                  });
                }
                window.materialStageAggregator.get(key).totalImpact += materialImpact * stagePercentage;
              });
            });
          } else if (component.materials && Array.isArray(component.materials)) {
            // Use actual material data from Excel

            if (aggregateResults) {
              // Store aggregated impacts for later processing
              if (!window.materialStageAggregator) {
                window.materialStageAggregator = new Map();
              }
              
              component.materials.forEach(material => {
                if (material.name) {
                  // Check if we have actual LCA stage data
                  const componentStages = lcaData.lcaStagesData?.[component.id];
                  if (componentStages && Object.keys(componentStages).length > 0) {
                    // Use actual data from Excel
                    const stageGroups = {
                      'A1-A3': ['A1', 'A2', 'A3'],
                      'A4-A5': ['A4', 'A5'],
                      'B1-B7': ['B1', 'B2', 'B3', 'B4', 'B5', 'B6', 'B7'],
                      'C1-C4': ['C1', 'C2', 'C3', 'C4'],
                      'D': ['D']
                    };

                    Object.entries(stageGroups).forEach(([groupName, stageCodes]) => {
                      // Sum up all values for this group
                      const groupTotal = stageCodes.reduce((sum, stageCode) => {
                        const stageValue = componentStages[stageCode];
                        return sum + (stageValue || 0); // Accept all values, including negatives
                      }, 0);

                      if (groupTotal !== 0) { // Changed from > 0 to !== 0 to include negative values
                        const key = `${material.name}_${groupName}`;
                        if (!window.materialStageAggregator.has(key)) {
                          window.materialStageAggregator.set(key, {
                            material: material.name,
                            stage: groupName,
                            totalImpact: 0
                          });
                        }
                        // Distribute the component's LCA stage impact proportionally among materials
                        const materialPercentage = (material.percentage || 100) / 100;
                        const materialStageImpact = groupTotal * materialPercentage;
                        window.materialStageAggregator.get(key).totalImpact += materialStageImpact;
                      }
                    });
                  } else {
                    // Use material-based distribution for Excel data without LCA stages
                    const stageGroups = ['A1-A3', 'A4-A5', 'B1-B7', 'C1-C4', 'D'];
                    stageGroups.forEach(groupName => {
                      const stageDistribution = {
                        'A1-A3': 0.4,  // 40% to product stage
                        'A4-A5': 0.2,  // 20% to construction
                        'B1-B7': 0.25, // 25% to use stage
                        'C1-C4': 0.1,  // 10% to end-of-life
                        'D': 0.05      // 5% to benefits/loads
                      };
                      
                      const stageValue = impact * stageDistribution[groupName];
                      const key = `${material.name}_${groupName}`;
                      if (!window.materialStageAggregator.has(key)) {
                        window.materialStageAggregator.set(key, {
                          material: material.name,
                          stage: groupName,
                          totalImpact: 0
                        });
                      }
                      window.materialStageAggregator.get(key).totalImpact += stageValue;
                    });
                  }
                }
              });
            } else {
              // Create individual links (original behavior)
              component.materials.forEach(material => {
                if (material.name) {
                  const materialId = `material_${material.name.replace(/[^a-zA-Z0-9]/g, '_')}`;
                  
                  // Check if we have actual LCA stage data
                  const componentStages = lcaData.lcaStagesData?.[component.id];
                  if (componentStages && Object.keys(componentStages).length > 0) {
                    // Use actual data from Excel
                    const stageGroups = {
                      'A1-A3': ['A1', 'A2', 'A3'],
                      'A4-A5': ['A4', 'A5'],
                      'B1-B7': ['B1', 'B2', 'B3', 'B4', 'B5', 'B6', 'B7'],
                      'C1-C4': ['C1', 'C2', 'C3', 'C4'],
                      'D': ['D']
                    };

                    Object.entries(stageGroups).forEach(([groupName, stageCodes]) => {
                      // Sum up all values for this group
                      const groupTotal = stageCodes.reduce((sum, stageCode) => {
                        const stageValue = componentStages[stageCode];
                        return sum + (stageValue || 0); // Accept all values, including negatives
                      }, 0);

                      if (groupTotal !== 0) { // Changed from > 0 to !== 0 to include negative values
                        const stageId = `stage_${groupName.replace(/[^a-zA-Z0-9]/g, '_')}`;
                        
                        links.push({
                          source: nodeMap.get(materialId),
                          target: nodeMap.get(stageId),
                          value: groupTotal,
                          type: 'material-lca_stage'
                        });
                      }
                    });
                  } else {
                    // Create sample links for sample data
                    const stageGroups = ['A1-A3', 'A4-A5', 'B1-B7', 'C1-C4', 'D'];
                    stageGroups.forEach(groupName => {
                      const stageId = `stage_${groupName.replace(/[^a-zA-Z0-9]/g, '_')}`;
                      const stageNode = nodeMap.get(stageId);
                      
                      if (stageNode) {
                        // Distribute impact across stages (sample distribution)
                        const stageDistribution = {
                          'A1-A3': 0.4,  // 40% to product stage
                          'A4-A5': 0.2,  // 20% to construction
                          'B1-B7': 0.25, // 25% to use stage
                          'C1-C4': 0.1,  // 10% to end-of-life
                          'D': 0.05      // 5% to benefits/loads
                        };
                        
                        const stageValue = impact * stageDistribution[groupName];
                        
                        links.push({
                          source: nodeMap.get(materialId),
                          target: stageNode,
                          value: stageValue,
                          type: 'material-lca_stage'
                        });
                      }
                    });
                  }
                }
              });
            }
          }
        }
      });
      
      // For aggregated data (both sample and Excel), now create the aggregated links (exactly 5 per material)

      if (window.materialStageAggregator) {
        console.log('Creating aggregated Materials → LCA Stages links...');
        
        // Group by material
        const materialStageMap = new Map();
        window.materialStageAggregator.forEach((data, key) => {
          const { material, stage, totalImpact } = data;
          if (!materialStageMap.has(material)) {
            materialStageMap.set(material, new Map());
          }
          materialStageMap.get(material).set(stage, totalImpact);
        });
        
        // Create exactly 5 links per material (one to each LCA stage)
        materialStageMap.forEach((stageMap, materialName) => {
          const materialId = `material_${materialName.replace(/[^a-zA-Z0-9]/g, '_')}`;
          const materialNode = nodeMap.get(materialId);
          
          if (materialNode) {
            console.log(`Creating 5 aggregated links for ${materialName}:`);
            
            // Ensure we have all 5 LCA stages
            const allStages = ['A1-A3', 'A4-A5', 'B1-B7', 'C1-C4', 'D'];
            allStages.forEach(stageName => {
              const stageId = `stage_${stageName.replace(/[^a-zA-Z0-9]/g, '_')}`;
              const stageNode = nodeMap.get(stageId);
              
              if (stageNode) {
                const stageValue = stageMap.get(stageName) || 0;
                if (stageValue > 0) {
                  links.push({
                    source: materialNode,
                    target: stageNode,
                    value: stageValue,
                    type: 'material-lca_stage'
                  });
                  console.log(`  → ${stageName}: ${stageValue.toFixed(1)} kg CO₂-eq`);
                }
              }
            });
          }
        });
        
        console.log(`Materials → LCA Stages: Created ${links.filter(l => l.type === 'material-lca_stage').length} aggregated links`);
        
        // Clear the aggregator for next use
        window.materialStageAggregator.clear();
      }
    }

        // Components -> LCA Stages (if materials is hidden but LCA stages are visible)
    // Use actual LCA stage data when available, otherwise use material-based distribution
    if (!visibleColumns.materials && visibleColumns.lcaStages && visibleColumns.components) {
    lcaData.components.forEach(component => {
        const impact = component.impacts[selectedImpact] || 0;
        if (impact > 0) {
          // Check if we have actual LCA stage data
          const componentStages = lcaData.lcaStagesData?.[component.id];
          if (componentStages && Object.keys(componentStages).length > 0) {
            // Use actual data from Excel
            const stageGroups = {
              'A1-A3': ['A1', 'A2', 'A3'],
              'A4-A5': ['A4', 'A5'],
              'B1-B7': ['B1', 'B2', 'B3', 'B4', 'B5', 'B6', 'B7'],
              'C1-C4': ['C1', 'C2', 'C3', 'C4'],
              'D': ['D']
            };

            Object.entries(stageGroups).forEach(([groupName, stageCodes]) => {
              // Sum up all values for this group
              const groupTotal = stageCodes.reduce((sum, stageCode) => {
                const stageValue = componentStages[stageCode];
                return sum + (stageValue || 0); // Accept all values, including negatives
              }, 0);

              if (groupTotal !== 0) { // Changed from > 0 to !== 0 to include negative values
                const stageId = `stage_${groupName.replace(/[^a-zA-Z0-9]/g, '_')}`;
                
                links.push({
                  source: nodeMap.get(`component_${component.id}`),
                  target: nodeMap.get(stageId),
                  value: groupTotal,
                  type: 'component-lca_stage'
                });
              }
            });
          } else {
            // Create sample links for sample data
            const stageGroups = ['A1-A3', 'A4-A5', 'B1-B7', 'C1-C4', 'D'];
            stageGroups.forEach(groupName => {
              const stageId = `stage_${groupName.replace(/[^a-zA-Z0-9]/g, '_')}`;
              const stageNode = nodeMap.get(stageId);
              
              if (stageNode) {
                // Distribute impact across stages (sample distribution)
                const stageDistribution = {
                  'A1-A3': 0.4,  // 40% to product stage
                  'A4-A5': 0.2,  // 20% to construction
                  'B1-B7': 0.25, // 25% to use stage
                  'C1-C4': 0.1,  // 10% to end-of-life
                  'D': 0.05      // 5% to benefits/loads
                };
                
                const stageValue = impact * stageDistribution[groupName];
                
                links.push({
                  source: nodeMap.get(`component_${component.id}`),
                  target: stageNode,
                  value: stageValue,
                  type: 'component-lca_stage'
                });
              }
            });
          }
        }
      });
    }
    
    // Handle skipped connections when columns are hidden
    // Building -> Components (if categories are hidden)
    if (visibleColumns.building && !visibleColumns.categories && visibleColumns.components) {
      categories.forEach(category => {
        const categoryComponents = lcaData.components.filter(c => c.category === category);
        categoryComponents.forEach(component => {
          const impact = component.impacts[selectedImpact] || 0;
          if (impact > 0) {
            links.push({
              source: nodeMap.get('building'),
          target: nodeMap.get(`component_${component.id}`),
          value: impact,
              type: 'building-component'
        });
      }
    });
      });
    }
    
    // Building -> Materials (when building and materials are visible, and BOTH categories AND components are hidden)
    // This shows the direct building impact to materials, useful for:
    // - Sample data page when you want to see building → materials flow (hide both categories and components)
    // - Excel data when you hide intermediate columns for cleaner visualization
    if (visibleColumns.building && visibleColumns.materials && !visibleColumns.categories && !visibleColumns.components) {
      categories.forEach(category => {
        const categoryComponents = lcaData.components.filter(c => c.category === category);
        categoryComponents.forEach(component => {
          const impact = component.impacts[selectedImpact] || 0;
          if (impact > 0) {
            if (isSampleData) {
              // For sample data, use realistic material distribution
              const componentMaterial = component.material; // Use actual material from mock data
              
              // Get the same material distribution as in component-to-material links
              let materialDistribution = [];
              
              if (componentMaterial === 'Concrete') {
                materialDistribution.push({ material: 'Concrete', percentage: 0.85 });
                materialDistribution.push({ material: 'Steel', percentage: 0.15 });
              } else if (componentMaterial === 'Steel') {
                materialDistribution.push({ material: 'Steel', percentage: 0.90 });
                materialDistribution.push({ material: 'Paint', percentage: 0.10 });
              } else if (componentMaterial === 'Brick') {
                materialDistribution.push({ material: 'Ceramic', percentage: 0.70 });
                materialDistribution.push({ material: 'Concrete', percentage: 0.25 });
                materialDistribution.push({ material: 'Paint', percentage: 0.05 });
              } else if (componentMaterial === 'Glass') {
                materialDistribution.push({ material: 'Glass', percentage: 0.80 });
                materialDistribution.push({ material: 'Steel', percentage: 0.15 });
                materialDistribution.push({ material: 'Plastic', percentage: 0.05 });
              } else if (componentMaterial === 'Mineral Wool') {
                materialDistribution.push({ material: 'Insulation', percentage: 1.0 });
              } else if (componentMaterial === 'Copper') {
                materialDistribution.push({ material: 'Steel', percentage: 0.60 });
                materialDistribution.push({ material: 'Paint', percentage: 0.40 });
              } else if (componentMaterial === 'Ceramic') {
                materialDistribution.push({ material: 'Ceramic', percentage: 0.95 });
                materialDistribution.push({ material: 'Concrete', percentage: 0.05 });
              } else if (componentMaterial === 'Gypsum') {
                materialDistribution.push({ material: 'Paint', percentage: 0.60 });
                materialDistribution.push({ material: 'Steel', percentage: 0.30 });
                materialDistribution.push({ material: 'Insulation', percentage: 0.10 });
              } else if (componentMaterial === 'Acoustic Panels') {
                materialDistribution.push({ material: 'Wood', percentage: 0.50 });
                materialDistribution.push({ material: 'Insulation', percentage: 0.30 });
                materialDistribution.push({ material: 'Steel', percentage: 0.20 });
              } else if (componentMaterial === 'Low-VOC Paint') {
                materialDistribution.push({ material: 'Paint', percentage: 1.0 });
              } else {
                materialDistribution.push({ material: 'Steel', percentage: 1.0 });
              }
              
              // Create building-to-material links using realistic distribution
              materialDistribution.forEach(({ material: materialName, percentage }) => {
                const materialId = `material_${materialName.replace(/[^a-zA-Z0-9]/g, '_')}`;
                const materialNode = nodeMap.get(materialId);
                
                if (materialNode) {
                  const materialValue = impact * percentage;
                  
                  links.push({
                    source: nodeMap.get('building'),
                    target: materialNode,
                    value: materialValue,
                    type: 'building-material'
                  });
                }
              });
            } else {
              // For Excel data, use the simple material detection
        const material = getMaterialFromComponent(component.name);
        const materialId = `material_${material.replace(/[^a-zA-Z0-9]/g, '_')}`;
        
        links.push({
                source: nodeMap.get('building'),
          target: nodeMap.get(materialId),
          value: impact,
                type: 'building-material'
              });
            }
          }
        });
      });
    }
    
    // Categories -> Materials (if components are hidden)
    if (visibleColumns.categories && !visibleColumns.components && visibleColumns.materials) {
      categories.forEach(category => {
        const categoryComponents = lcaData.components.filter(c => c.category === category);
        categoryComponents.forEach(component => {
      const impact = component.impacts[selectedImpact] || 0;
      if (impact > 0) {
            if (isSampleData) {
              // For sample data, use realistic material distribution
              const componentMaterial = component.material; // Use actual material from mock data
              
              // Get the same material distribution as in component-to-material links
              let materialDistribution = [];
              
              if (componentMaterial === 'Concrete') {
                materialDistribution.push({ material: 'Concrete', percentage: 0.85 });
                materialDistribution.push({ material: 'Steel', percentage: 0.15 });
              } else if (componentMaterial === 'Steel') {
                materialDistribution.push({ material: 'Steel', percentage: 0.90 });
                materialDistribution.push({ material: 'Paint', percentage: 0.10 });
              } else if (componentMaterial === 'Brick') {
                materialDistribution.push({ material: 'Ceramic', percentage: 0.70 });
                materialDistribution.push({ material: 'Concrete', percentage: 0.25 });
                materialDistribution.push({ material: 'Paint', percentage: 0.05 });
              } else if (componentMaterial === 'Glass') {
                materialDistribution.push({ material: 'Glass', percentage: 0.80 });
                materialDistribution.push({ material: 'Steel', percentage: 0.15 });
                materialDistribution.push({ material: 'Plastic', percentage: 0.05 });
              } else if (componentMaterial === 'Mineral Wool') {
                materialDistribution.push({ material: 'Insulation', percentage: 1.0 });
              } else if (componentMaterial === 'Copper') {
                materialDistribution.push({ material: 'Steel', percentage: 0.60 });
                materialDistribution.push({ material: 'Plastic', percentage: 0.40 });
              } else if (componentMaterial === 'Ceramic') {
                materialDistribution.push({ material: 'Ceramic', percentage: 0.95 });
                materialDistribution.push({ material: 'Concrete', percentage: 0.05 });
              } else if (componentMaterial === 'Gypsum') {
                materialDistribution.push({ material: 'Paint', percentage: 0.60 });
                materialDistribution.push({ material: 'Steel', percentage: 0.30 });
                materialDistribution.push({ material: 'Insulation', percentage: 0.10 });
              } else if (componentMaterial === 'Acoustic Panels') {
                materialDistribution.push({ material: 'Wood', percentage: 0.50 });
                materialDistribution.push({ material: 'Insulation', percentage: 0.30 });
                materialDistribution.push({ material: 'Steel', percentage: 0.20 });
              } else if (componentMaterial === 'Low-VOC Paint') {
                materialDistribution.push({ material: 'Paint', percentage: 1.0 });
              } else {
                materialDistribution.push({ material: 'Steel', percentage: 1.0 });
              }
              
              // Create category-to-material links using realistic distribution
              materialDistribution.forEach(({ material: materialName, percentage }) => {
                const materialId = `material_${materialName.replace(/[^a-zA-Z0-9]/g, '_')}`;
                const materialNode = nodeMap.get(materialId);
                
                if (materialNode) {
                  const materialValue = impact * percentage;
                  
                  links.push({
                    source: nodeMap.get(`category_${category}`),
                    target: materialNode,
                    value: materialValue,
                    type: 'category-material'
                  });
                }
              });
            } else {
              // For Excel data, use the simple material detection
        const material = getMaterialFromComponent(component.name);
              const materialId = `material_${material.replace(/[^a-zA-Z0-9]/g, '_')}`;
              
              links.push({
                source: nodeMap.get(`category_${category}`),
                target: nodeMap.get(materialId),
                value: impact,
                type: 'category-material'
              });
            }
          }
        });
      });
    }
    
    // Categories -> LCA Stages (if components AND materials are hidden)
    if (visibleColumns.categories && !visibleColumns.components && !visibleColumns.materials && visibleColumns.lcaStages) {
      console.log('Creating Categories → LCA Stages flow...');
      categories.forEach(category => {
        const categoryComponents = lcaData.components.filter(c => c.category === category);
        console.log(`Category "${category}" has ${categoryComponents.length} components`);
        categoryComponents.forEach(component => {
          const impact = component.impacts[selectedImpact] || 0;
          if (impact > 0) {
            if (!isSampleData && lcaData.lcaStagesData && component.id) {
              // For Excel data, use actual LCA stage data

              if (aggregateResults) {
                // Store aggregated impacts for later processing
                if (!window.categoryStageAggregator) {
                  window.categoryStageAggregator = new Map();
                }
                
                const componentStages = lcaData.lcaStagesData[component.id];
                if (componentStages && Object.keys(componentStages).length > 0) {
                  const stageGroups = {
                    'A1-A3': ['A1', 'A2', 'A3'],
                    'A4-A5': ['A4', 'A5'],
                    'B1-B7': ['B1', 'B2', 'B3', 'B4', 'B5', 'B6', 'B7'],
                    'C1-C4': ['C1', 'C2', 'C3', 'C4'],
                    'D': ['D']
                  };

                  Object.entries(stageGroups).forEach(([groupName, stageCodes]) => {
                    // Sum up all values for this group
                    const groupTotal = stageCodes.reduce((sum, stageCode) => {
                      const stageValue = componentStages[stageCode];
                      return sum + (stageValue || 0); // Accept all values, including negatives
                    }, 0);

                    if (groupTotal !== 0) { // Changed from > 0 to !== 0 to include negative values
                      const key = `${category}_${groupName}`;
                      if (!window.categoryStageAggregator.has(key)) {
                        window.categoryStageAggregator.set(key, {
                          category: category,
                          stage: groupName,
                          totalImpact: 0
                        });
                      }
                      window.categoryStageAggregator.get(key).totalImpact += groupTotal;
                    }
                  });
                }
              } else {
                // Create individual links (original behavior)
                const componentStages = lcaData.lcaStagesData[component.id];
                if (componentStages && Object.keys(componentStages).length > 0) {
                  const stageGroups = {
                    'A1-A3': ['A1', 'A2', 'A3'],
                    'A4-A5': ['A4', 'A5'],
                    'B1-B7': ['B1', 'B2', 'B3', 'B4', 'B5', 'B6', 'B7'],
                    'C1-C4': ['C1', 'C2', 'C3', 'C4'],
                    'D': ['D']
                  };

                  Object.entries(stageGroups).forEach(([groupName, stageCodes]) => {
                    // Sum up all values for this group
                    const groupTotal = stageCodes.reduce((sum, stageCode) => {
                      const stageValue = componentStages[stageCode];
                      return sum + (stageValue || 0); // Accept all values, including negatives
                    }, 0);

                    if (groupTotal !== 0) { // Changed from > 0 to !== 0 to include negative values
                      const stageId = `stage_${groupName.replace(/[^a-zA-Z0-9]/g, '_')}`;
                      const stageNode = nodeMap.get(stageId);
                      
                      if (stageNode) {
                        links.push({
                          source: nodeMap.get(`category_${category}`),
                          target: stageNode,
                          value: groupTotal,
                          type: 'category-lca_stage',
                          componentName: component.name
                        });
                      }
                    }
                  });
                } else {
                  // Fallback: use material-based distribution for Excel data without LCA stages
                  console.log(`Component "${component.name}" has no LCA stage data, using material-based fallback`);
                  const material = getMaterialFromComponent(component.name);
      const stageDistribution = getStageDistribution(material);
      
                  if (stageDistribution && Object.keys(stageDistribution).length > 0) {
      Object.entries(stageDistribution).forEach(([stageId, percentage]) => {
                      const stageImpact = impact * percentage;
                      if (stageImpact > 0) {
                        const stageNode = nodeMap.get(stageId);
                        if (stageNode) {
          links.push({
                            source: nodeMap.get(`category_${category}`),
                            target: stageNode,
            value: stageImpact,
                            type: 'category-lca_stage',
                            componentName: component.name
                          });
                        }
                      }
                    });
                  } else {
                    console.warn(`No stage distribution found for material "${material}" in component "${component.name}"`);
                  }
                }
              }
            } else {
              // For sample data, we need to aggregate all component contributions to each category
              // Store the aggregated impacts for later processing
              if (!window.categoryStageAggregator) {
                window.categoryStageAggregator = new Map();
              }
              
              const componentMaterial = component.material || 'Steel'; // Use actual material from mock data, fallback to Steel
              console.log(`Sample data: Component "${component.name}" uses material "${componentMaterial}"`);
              
              // Get the same material distribution as in component-to-material links
              let materialDistribution = [];
              
              if (componentMaterial === 'Concrete') {
                materialDistribution.push({ material: 'Concrete', percentage: 0.85 });
                materialDistribution.push({ material: 'Steel', percentage: 0.15 });
              } else if (componentMaterial === 'Steel') {
                materialDistribution.push({ material: 'Steel', percentage: 0.90 });
                materialDistribution.push({ material: 'Paint', percentage: 0.10 });
              } else if (componentMaterial === 'Brick') {
                materialDistribution.push({ material: 'Ceramic', percentage: 0.70 });
                materialDistribution.push({ material: 'Concrete', percentage: 0.25 });
                materialDistribution.push({ material: 'Paint', percentage: 0.05 });
              } else if (componentMaterial === 'Glass') {
                materialDistribution.push({ material: 'Glass', percentage: 0.80 });
                materialDistribution.push({ material: 'Steel', percentage: 0.15 });
                materialDistribution.push({ material: 'Plastic', percentage: 0.05 });
              } else if (componentMaterial === 'Mineral Wool') {
                materialDistribution.push({ material: 'Insulation', percentage: 1.0 });
              } else if (componentMaterial === 'Copper') {
                materialDistribution.push({ material: 'Steel', percentage: 0.60 });
                materialDistribution.push({ material: 'Plastic', percentage: 0.40 });
              } else if (componentMaterial === 'Ceramic') {
                materialDistribution.push({ material: 'Ceramic', percentage: 0.95 });
                materialDistribution.push({ material: 'Concrete', percentage: 0.05 });
              } else if (componentMaterial === 'Gypsum') {
                materialDistribution.push({ material: 'Paint', percentage: 0.60 });
                materialDistribution.push({ material: 'Steel', percentage: 0.30 });
                materialDistribution.push({ material: 'Insulation', percentage: 0.10 });
              } else if (componentMaterial === 'Acoustic Panels') {
                materialDistribution.push({ material: 'Wood', percentage: 0.50 });
                materialDistribution.push({ material: 'Insulation', percentage: 0.30 });
                materialDistribution.push({ material: 'Steel', percentage: 0.20 });
              } else if (componentMaterial === 'Low-VOC Paint') {
                materialDistribution.push({ material: 'Paint', percentage: 1.0 });
              } else if (componentMaterial === 'Wood') {
                materialDistribution.push({ material: 'Wood', percentage: 0.80 });
                materialDistribution.push({ material: 'Steel', percentage: 0.20 });
              } else if (componentMaterial === 'Insulation') {
                materialDistribution.push({ material: 'Insulation', percentage: 1.0 });
              } else if (componentMaterial === 'Paint') {
                materialDistribution.push({ material: 'Paint', percentage: 1.0 });
              } else if (componentMaterial === 'Plastic') {
                materialDistribution.push({ material: 'Plastic', percentage: 1.0 });
              } else {
                // Fallback for any unknown materials
                materialDistribution.push({ material: 'Steel', percentage: 1.0 });
              }
              
              // Aggregate impacts for each category and LCA stage
              materialDistribution.forEach(({ material: materialName, percentage }) => {
                const materialImpact = impact * percentage;
                
                // Create realistic LCA stage distribution based on material type
                let stageDistribution = {};
                if (materialName === 'Concrete') {
                  stageDistribution = { 'A1-A3': 0.7, 'A4-A5': 0.15, 'B1-B7': 0.1, 'C1-C4': 0.04, 'D': 0.01 };
                } else if (materialName === 'Steel') {
                  stageDistribution = { 'A1-A3': 0.6, 'A4-A5': 0.2, 'B1-B7': 0.15, 'C1-C4': 0.03, 'D': 0.02 };
                } else if (materialName === 'Glass') {
                  stageDistribution = { 'A1-A3': 0.65, 'A4-A5': 0.18, 'B1-B7': 0.12, 'C1-C4': 0.03, 'D': 0.02 };
                } else if (materialName === 'Ceramic') {
                  stageDistribution = { 'A1-A3': 0.75, 'A4-A5': 0.15, 'B1-B7': 0.08, 'C1-C4': 0.01, 'D': 0.01 };
                } else if (materialName === 'Wood') {
                  stageDistribution = { 'A1-A3': 0.5, 'A4-A5': 0.2, 'B1-B7': 0.2, 'C1-C4': 0.08, 'D': 0.02 };
                } else if (materialName === 'Insulation') {
                  stageDistribution = { 'A1-A3': 0.6, 'A4-A5': 0.15, 'B1-B7': 0.2, 'C1-C4': 0.03, 'D': 0.02 };
                } else if (materialName === 'Paint') {
                  stageDistribution = { 'A1-A3': 0.7, 'A4-A5': 0.15, 'B1-B7': 0.1, 'C1-C4': 0.03, 'D': 0.02 };
                } else if (materialName === 'Plastic') {
                  stageDistribution = { 'A1-A3': 0.65, 'A4-A5': 0.15, 'B1-B7': 0.15, 'C1-C4': 0.03, 'D': 0.02 };
                } else {
                  // Default distribution for other materials
                  stageDistribution = { 'A1-A3': 0.4, 'A4-A5': 0.2, 'B1-B7': 0.25, 'C1-C4': 0.1, 'D': 0.05 };
                }
                
                // Aggregate the impacts for each stage
                Object.entries(stageDistribution).forEach(([groupName, stagePercentage]) => {
                  const key = `${category}_${groupName}`;
                  if (!window.categoryStageAggregator.has(key)) {
                    window.categoryStageAggregator.set(key, {
                      category: category,
                      stage: groupName,
                      totalImpact: 0
                    });
                  }
                  window.categoryStageAggregator.get(key).totalImpact += materialImpact * stagePercentage;
                });
              });
            }
        }
      });
    });

      // For aggregated data (both sample and Excel), now create the aggregated links (exactly 5 per category)

      if (window.categoryStageAggregator) {
        console.log('Creating aggregated Categories → LCA Stages links...');
        
        // Group by category
        const categoryStageMap = new Map();
        window.categoryStageAggregator.forEach((data, key) => {
          const { category, stage, totalImpact } = data;
          if (!categoryStageMap.has(category)) {
            categoryStageMap.set(category, new Map());
          }
          categoryStageMap.get(category).set(stage, totalImpact);
        });
        
        // Create exactly 5 links per category (one to each LCA stage)
        categoryStageMap.forEach((stageMap, categoryName) => {
          const categoryNode = nodeMap.get(`category_${categoryName}`);
          
          if (categoryNode) {
            console.log(`Creating 5 aggregated links for category "${categoryName}":`);
            
            // Ensure we have all 5 LCA stages
            const allStages = ['A1-A3', 'A4-A5', 'B1-B7', 'C1-C4', 'D'];
            allStages.forEach(stageName => {
              const stageId = `stage_${stageName.replace(/[^a-zA-Z0-9]/g, '_')}`;
              const stageNode = nodeMap.get(stageId);
              
              if (stageNode) {
                const stageValue = stageMap.get(stageName) || 0;
                if (stageValue > 0) {
                  links.push({
                    source: categoryNode,
                    target: stageNode,
                    value: stageValue,
                    type: 'category-lca_stage'
                  });
                  console.log(`  → ${stageName}: ${stageValue.toFixed(1)} kg CO₂-eq`);
                }
              }
            });
          }
        });
        
        console.log(`Categories → LCA Stages: Created ${links.filter(l => l.type === 'category-lca_stage').length} aggregated links`);
        
        // Clear the aggregator for next use
        window.categoryStageAggregator.clear();
      }
      
      console.log(`Categories → LCA Stages flow complete. Created ${links.filter(l => l.type === 'category-lca_stage').length} links`);
    }

    // Components -> LCA Stages flow is now handled above with unified logic

    // Final validation of nodes and links
    const validNodes = nodes.filter(node => {
      if (!node || !node.id || !node.name) {
        console.warn('Invalid node found:', node);
        return false;
      }
      return true;
    });
    
    const validLinks = links.filter(link => {
      if (!link || !link.source || !link.target || !link.value || isNaN(link.value) || link.value <= 0) {
        console.warn('Invalid link found:', link);
        return false;
      }
      return true;
    });
    
    console.log('Final sankey data:', { 
      nodes: validNodes.length, 
      links: validLinks.length,
      nodeIds: validNodes.map(n => n.id),
      linkTypes: validLinks.map(l => l.type)
    });
    
    return { nodes: validNodes, links: validLinks };
  }, [lcaData, selectedImpact, getMaterialFromComponent, getStageDistribution, getStageDescription, visibleColumns, aggregateResults]);

  // Debounced render function for performance
  const debouncedRender = useCallback(
    debounce(() => {
      if (!sankeyData.nodes.length || !svgRef.current) return;

      // Clear previous content
      d3.select(svgRef.current).selectAll("*").remove();

      const margin = { top: 30, right: 30, bottom: 30, left: 30 };
      const width = 1200 - margin.left - margin.right;
      const height = 800 - margin.top - margin.bottom;

      const svg = d3.select(svgRef.current)
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .on('mouseleave', function() {
          // Clear all hover effects when mouse leaves the entire chart
          d3.selectAll('.sankey-link')
            .classed('hovered', false)
            .attr('opacity', 0.7)
            .attr('stroke-width', d => Math.max(2, d.width + 1))
            .attr('filter', 'none');
          
          d3.selectAll('.sankey-node')
            .classed('hovered', false)
            .attr('opacity', 1)
            .attr('filter', 'none');
          
          // Remove any tooltips
          d3.selectAll('.sankey-tooltip').remove();
        })
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      // Create Sankey layout with optimized settings
       const sankeyLayout = sankey()
        .nodeWidth(20)
        .nodePadding(8)
        .extent([[1, 1], [width - 1, height - 5]])
        .nodeSort((a, b) => {
          // Custom sorting for LCA stage nodes to maintain order
          if (a.type === 'lca_stage' && b.type === 'lca_stage') {
            const stageOrder = ['A1-A3', 'A4-A5', 'B1-B7', 'C1-C4', 'D'];
            const aIndex = stageOrder.indexOf(a.group);
            const bIndex = stageOrder.indexOf(b.group);
            if (aIndex !== -1 && bIndex !== -1) {
              return aIndex - bIndex;
            }
          }
          return 0;
        });

      let nodes, links;
      try {
        const result = sankeyLayout(sankeyData);
        nodes = result.nodes;
        links = result.links;
        console.log('D3 Sankey processing successful:', { nodes: nodes.length, links: links.length });
      } catch (error) {
        console.error('D3 Sankey processing failed:', error);
        console.error('Sankey data that caused the error:', sankeyData);
        return; // Exit early if processing fails
      }

      // Color scale for different node types - uses category-based colors
      const colorScale = d3.scaleOrdinal()
        .domain(['building', 'category', 'component', 'material', 'lca_stage'])
        .range([buildingColor, '#FFD93D', '#6BCB77', '#4D96FF', '#843BFF']);

      // Batch DOM operations for better performance
      const linkGroup = svg.append('g').attr('class', 'links');
      const nodeGroup = svg.append('g').attr('class', 'nodes');
      const labelGroup = svg.append('g').attr('class', 'labels');

      // Draw links with optimized rendering
      linkGroup.selectAll('path')
        .data(links)
        .enter()
        .append('path')
        .attr('class', 'sankey-element sankey-link')
        .attr('data-source', d => d.source.id)
        .attr('data-target', d => d.target.id)
        .attr('d', sankeyLinkHorizontal())
        .attr('stroke', d => {
          let sourceColor;
          if (d.source.type === 'building') return d3.color(buildingColor).darker(0.3);
          if (d.source.type === 'category') return d3.color(categoryColors[d.source.name] || '#95A5A6').darker(0.3);
          if (d.source.type === 'component') {
            const component = lcaData.components.find(c => `component_${c.id}` === d.source.id);
            return component ? d3.color(categoryColors[component.category] || '#95A5A6').darker(0.3) : d3.color('#95A5A6').darker(0.3);
          }
          if (d.source.type === 'material') {
            // Find the source to get its category color
            const materialLinks = links.filter(l => l.target.id === d.source.id);
            if (materialLinks.length > 0) {
              const sourceLink = materialLinks[0];
              
              if (sourceLink.source.type === 'component') {
                // Components visible: get component's category color
                const component = lcaData.components.find(c => `component_${c.id}` === sourceLink.source.id);
                return component ? d3.color(categoryColors[component.category] || '#95A5A6').darker(0.3) : d3.color('#95A5A6').darker(0.3);
              } else if (sourceLink.source.type === 'category') {
                // Components hidden: get category color directly
                return d3.color(categoryColors[sourceLink.source.name] || '#95A5A6').darker(0.3);
              }
            }
            
            // If no direct source found, try to find through component material mapping
            if (isSampleData) {
              const materialName = d.source.name;
              // Find which component category this material belongs to
              for (const component of lcaData.components) {
                if (component.material === materialName) {
                  return d3.color(categoryColors[component.category] || '#95A5A6').darker(0.3);
                }
              }
            }
            
            return d3.color('#95A5A6').darker(0.3);
          }
          return d3.color(colorScale(d.source.type)).darker(0.3);
        })
        .attr('stroke-width', d => Math.max(3, d.width + 0.6))
        .attr('fill', 'none')
        .attr('opacity', 0.7)
        .on('mouseover', function(event, d) {
          // Clear all existing hover effects first
          d3.selectAll('.sankey-link')
            .classed('hovered', false)
            .attr('opacity', 0.7)
            .attr('stroke-width', d => Math.max(2, d.width + 1))
            .attr('filter', 'none');
          
          // Remove any existing tooltips
          d3.selectAll('.sankey-link-tooltip').remove();
          
          // Then apply hover to this element and bring it to front
          d3.select(this)
            .classed('hovered', true)
            .attr('opacity', 1)
            .attr('stroke-width', Math.max(3, d.width + 3))
            .attr('filter', 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))')
            .raise();
          
          // Show tooltip ONLY for Categories → LCA Stages links when aggregation is OFF
          if (!aggregateResults && d.source.type === 'category' && d.target.type === 'lca_stage') {
            // When aggregation is OFF, each link represents a single component
            // The link data now contains the componentName property
            if (d.componentName) {
              const tooltip = d3.select('body').append('div')
                .attr('class', 'sankey-link-tooltip')
                .style('position', 'absolute')
                .style('background', 'rgba(0, 0, 0, 0.8)')
                .style('color', 'white')
                .style('padding', '8px 12px')
                .style('border-radius', '6px')
                .style('font-size', '12px')
                .style('pointer-events', 'none')
                .style('z-index', '1000')
                .style('max-width', '200px')
                .style('white-space', 'nowrap');
              
              tooltip.text(d.componentName);
              
              // Position tooltip near the mouse
              const tooltipRect = tooltip.node().getBoundingClientRect();
              const x = Math.min(event.pageX + 10, window.innerWidth - tooltipRect.width - 10);
              const y = Math.max(event.pageY - tooltipRect.height - 10, 10);
              
              tooltip
                .style('left', x + 'px')
                .style('top', y + 'px');
            }
          }
        })
        .on('mouseout', function(event, d) {
          // Clear hover effect immediately when leaving this link
          d3.select(this)
            .classed('hovered', false)
            .attr('opacity', 0.7)
            .attr('stroke-width', Math.max(2, d.width + 1))
            .attr('filter', 'none');
          
          // Remove tooltip when leaving the link
          d3.selectAll('.sankey-link-tooltip').remove();
          
          // Re-render the entire chart to restore original link order
          // This ensures all links go back to their proper positions
          setTimeout(() => {
            debouncedRender();
          }, 50);
        });

      // Draw nodes with optimized rendering
      nodeGroup.selectAll('rect')
        .data(nodes)
        .enter()
        .append('rect')
        .attr('class', 'sankey-element sankey-node')
        .attr('data-id', d => d.id)
        .attr('x', d => d.x0)
        .attr('y', d => d.y0)
        .attr('height', d => d.y1 - d.y0)
        .attr('width', d => d.x1 - d.x0)
        .attr('fill', d => {
          if (d.type === 'building') return buildingColor;
          if (d.type === 'category') return categoryColors[d.name] || '#95A5A6';
          if (d.type === 'component') {
            const component = lcaData.components.find(c => `component_${c.id}` === d.id);
            return component ? (categoryColors[component.category] || '#95A5A6') : '#95A5A6';
          }
          if (d.type === 'material') {
            // Find the source to get its category color
            const materialLinks = links.filter(l => l.target.id === d.id);
            if (materialLinks.length > 0) {
              const sourceLink = materialLinks[0];
              
              if (sourceLink.source.type === 'component') {
                // Components visible: get component's category color
                const component = lcaData.components.find(c => `component_${c.id}` === sourceLink.source.id);
                return component ? (categoryColors[component.category] || '#95A5A6') : '#95A5A6';
              } else if (sourceLink.source.type === 'category') {
                // Components hidden: get category color directly
                return categoryColors[sourceLink.source.name] || '#95A5A6';
              }
            }
            
            // If no direct source found, try to find through component material mapping
            if (isSampleData) {
              const materialName = d.name;
              // Find which component category this material belongs to
              for (const component of lcaData.components) {
                if (component.material === materialName) {
                  return categoryColors[component.category] || '#95A5A6';
                }
              }
            }
            
            return '#95A5A6';
          }
          if (d.type === 'lca_stage') {
            // Find the source through materials to get category color
            const stageLinks = links.filter(l => l.target.id === d.id);
            if (stageLinks.length > 0) {
              const materialId = stageLinks[0].source.id;
              const materialLinks = links.filter(l => l.target.id === materialId);
              if (materialLinks.length > 0) {
                const sourceLink = materialLinks[0];
                
                if (sourceLink.source.type === 'component') {
                  // Components visible: get component's category color
                  const component = lcaData.components.find(c => `component_${c.id}` === sourceLink.source.id);
                  return component ? (categoryColors[component.category] || '#95A5A6') : '#95A5A6';
                } else if (sourceLink.source.type === 'category') {
                  // Components hidden: get category color directly
                  return categoryColors[sourceLink.source.name] || '#95A5A6';
                }
              }
              
              // If no direct source found, try to find through material's component mapping
              if (isSampleData) {
                const materialNode = nodes.find(n => n.id === materialId);
                if (materialNode && materialNode.name) {
                  const materialName = materialNode.name;
                  // Find which component category this material belongs to
                  for (const component of lcaData.components) {
                    if (component.material === materialName) {
                      return categoryColors[component.category] || '#95A5A6';
                    }
                  }
                }
              }
            }
            return '#95A5A6';
          }
          return colorScale(d.type);
        })

        .on('mouseover', function(event, d) {
          // Clear all existing hover effects first
          d3.selectAll('.sankey-node')
            .classed('hovered', false)
            .attr('opacity', 1)
            .attr('filter', 'none');
          
          // Remove any existing tooltips
          d3.selectAll('.sankey-tooltip').remove();
          
          // Then apply hover to this element and bring it to front
          d3.select(this)
            .classed('hovered', true)
            .attr('opacity', 0.8)
            .attr('filter', 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))')
            .raise();
          
          // Show tooltip with optimized positioning
          const tooltip = d3.select('body').append('div')
            .attr('class', 'sankey-tooltip')
            .style('position', 'absolute')
            .style('background', 'rgba(255, 255, 255, 0.95)')
            .style('color', '#1f2937')
            .style('padding', '12px')
            .style('border-radius', '8px')
            .style('font-size', '13px')
            .style('pointer-events', 'none')
            .style('z-index', '1000')
            .style('box-shadow', '0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)')
            .style('max-width', '280px')
            .style('line-height', '1.3')
            .style('border', '1px solid rgba(229, 231, 235, 0.8)')
            .style('backdrop-filter', 'blur(8px)');

          let tooltipContent = '';
          
          if (d.type === 'building') {
            const totalComponents = lcaData.components.length;
            const totalCategories = [...new Set(lcaData.components.map(c => c.category))].length;
            const buildingArea = lcaData.buildingInfo?.area || 'N/A';
            const serviceLife = lcaData.buildingInfo?.serviceLife || 'N/A';
            const totalImpact = lcaData.totalImpacts[selectedImpact] || 0;
            const impactInfo = lcaData.impactCategories.find(c => c.id === selectedImpact);
            
            tooltipContent = `
                <div style="font-weight: bold; color: #000000; margin-bottom: 8px; font-size: 14px;">${d.name}</div>
                <div style="margin: 8px 0; color: #000000;">
                  <div style="margin-bottom: 4px;"><span style="font-weight: 600;">Total ${impactInfo?.shortName || 'Impact'}:</span> <span style="color: #dc2626; font-weight: 700;">${totalImpact.toLocaleString()} ${impactInfo?.unit || ''}</span></div>
                  <div style="margin-bottom: 4px;"><span style="font-weight: 600;">Total Components:</span> ${totalComponents}</div>
                  <div style="margin-bottom: 4px;"><span style="font-weight: 600;">Categories:</span> ${totalCategories}</div>
                  <div style="margin-bottom: 4px;"><span style="font-weight: 600;">Building Area:</span> ${buildingArea} m²</div>
                  <div style="margin-bottom: 4px;"><span style="font-weight: 600;">Service Life:</span> ${serviceLife} years</div>
                </div>
                <div style="color: #666666; font-size: 11px; margin-top: 6px; padding-top: 6px; border-top: 1px solid #e5e7eb;">Root node connecting all building systems</div>
            `;
          } else if (d.type === 'category') {
            const categoryComponents = lcaData.components.filter(c => c.category === d.name);
            const componentCount = categoryComponents.length;
            const totalImpact = categoryComponents.reduce((sum, c) => sum + (c.impacts[selectedImpact] || 0), 0);
            const impactInfo = lcaData.impactCategories.find(c => c.id === selectedImpact);
            const percentage = lcaData.totalImpacts[selectedImpact] > 0 ? 
              ((totalImpact / lcaData.totalImpacts[selectedImpact]) * 100).toFixed(1) : 0;
            
            tooltipContent = `
                <div style="font-weight: bold; color: #000000; margin-bottom: 8px; font-size: 14px;">${d.name}</div>
                <div style="margin: 8px 0; color: #000000;">
                  <div style="margin-bottom: 4px;"><span style="font-weight: 600;">Components:</span> ${componentCount}</div>
                  <div style="margin-bottom: 4px;"><span style="font-weight: 600;">Total ${impactInfo?.shortName || 'Impact'}:</span> ${totalImpact.toLocaleString()} ${impactInfo?.unit || ''}</div>
                  <div style="margin-bottom: 4px;"><span style="font-weight: 600;">Contribution:</span> <span style="color: #dc2626; font-weight: 700;">${percentage}%</span></div>
                </div>
                <div style="color: #666666; font-size: 11px; margin-top: 6px; padding-top: 6px; border-top: 1px solid #e5e7eb;">Component grouping by function</div>
            `;
          } else if (d.type === 'component') {
            const component = lcaData.components.find(c => `component_${c.id}` === d.id);
            if (component) {
              const impact = component.impacts[selectedImpact] || 0;
              const impactInfo = lcaData.impactCategories.find(c => c.id === selectedImpact);
              const percentage = lcaData.totalImpacts[selectedImpact] > 0 ? 
                ((impact / lcaData.totalImpacts[selectedImpact]) * 100).toFixed(1) : 0;
              
              tooltipContent = `
                <div style="font-weight: bold; color: #000000; margin-bottom: 8px; font-size: 14px;">${d.name}</div>
                <div style="margin: 8px 0; color: #000000;">
                  <div style="margin-bottom: 4px;"><span style="font-weight: 600;">Category:</span> ${component.category}</div>
                  <div style="margin-bottom: 4px;"><span style="font-weight: 600;">${impactInfo?.shortName || 'Impact'}:</span> ${impact.toLocaleString()} ${impactInfo?.unit || ''}</div>
                  <div style="margin-bottom: 4px;"><span style="font-weight: 600;">Contribution:</span> <span style="color: #dc2626; font-weight: 700;">${percentage}%</span></div>
                </div>
                <div style="color: #666666; font-size: 11px; margin-top: 6px; padding-top: 6px; border-top: 1px solid #e5e7eb;">Individual building element</div>
              `;
            }
          } else if (d.type === 'material') {
            let totalImpact = 0;
            let usingComponents = [];
            const impactInfo = lcaData.impactCategories.find(c => c.id === selectedImpact);
            
            if (isSampleData) {
              // For sample data, calculate impact from the links that lead to this material
              const incomingLinks = sankeyData.links.filter(link => link.target.id === d.id);
              totalImpact = incomingLinks.reduce((sum, link) => sum + link.value, 0);
              
              // Find all components that contribute to this material via realistic distribution
              const contributingComponents = new Set();
              incomingLinks.forEach(link => {
                if (link.source.type === 'component') {
                  const component = lcaData.components.find(c => `component_${c.id}` === link.source.id);
                  if (component) contributingComponents.add(component);
                } else if (link.source.type === 'category') {
                  // If coming from category, find all components in that category
                  const categoryComponents = lcaData.components.filter(c => c.category === link.source.name);
                  categoryComponents.forEach(comp => contributingComponents.add(comp));
                }
              });
              usingComponents = Array.from(contributingComponents);
            } else {
              // For Excel data, use the simple mapping approach
              usingComponents = lcaData.components.filter(c => 
                getMaterialFromComponent(c.name) === d.name
              );
              totalImpact = usingComponents.reduce((sum, c) => sum + (c.impacts[selectedImpact] || 0), 0);
            }
            
            const componentCount = usingComponents.length;
            
            tooltipContent = `
                <div style="font-weight: bold; color: #000000; margin-bottom: 8px; font-size: 14px;">${d.name}</div>
                <div style="margin: 8px 0; color: #000000;">
                  <div style="margin-bottom: 4px;"><span style="font-weight: 600;">Used in:</span> ${componentCount} component${componentCount !== 1 ? 's' : ''}</div>
                  <div style="margin-bottom: 4px;"><span style="font-weight: 600;">Total ${impactInfo?.shortName || 'Impact'}:</span> ${totalImpact.toLocaleString()} ${impactInfo?.unit || ''}</div>
                  <div style="margin-bottom: 4px;"><span style="font-weight: 600;">Components:</span> ${usingComponents.map(c => c.name).join(', ')}</div>
                </div>
                <div style="color: #666666; font-size: 11px; margin-top: 6px; padding-top: 6px; border-top: 1px solid #e5e7eb;">Primary material type</div>
            `;
          } else if (d.type === 'lca_stage') {
            // Count components that contribute to this stage group
            const relatedComponents = new Set();
            const stageGroup = d.group;
            
            // Get stage codes for this group
            const stageCodes = stageGroup === 'A1-A3' ? ['A1', 'A2', 'A3'] :
                              stageGroup === 'A4-A5' ? ['A4', 'A5'] :
                              stageGroup === 'B1-B7' ? ['B1', 'B2', 'B3', 'B4', 'B5', 'B6', 'B7'] :
                              stageGroup === 'C1-C4' ? ['C1', 'C2', 'C3', 'C4'] :
                              ['D'];
            
            // Calculate total impact for this stage group
            let totalImpact = 0;
            
            // Always calculate impact from the actual links that lead to this stage
            // This ensures tooltip matches what's visually displayed in the Sankey diagram
            // Look for ALL incoming links (both component-lca_stage and material-lca_stage)
            const incomingLinks = sankeyData.links.filter(link => link.target.id === d.id);
            totalImpact = incomingLinks.reduce((sum, link) => sum + link.value, 0);
            
            // Count components that contribute to this stage
            if (isSampleData) {
              // For sample data, count all components
              lcaData.components.forEach(component => {
                if (component.impacts[selectedImpact] > 0) {
                  relatedComponents.add(component.name);
                }
              });
            } else {
              // For Excel data, count components that have data in this stage group
              Object.keys(lcaData.lcaStagesData || {}).forEach(componentId => {
                const componentStages = (lcaData.lcaStagesData || {})[componentId];
                const hasDataInGroup = componentStages && stageCodes.some(stageCode => {
                  const stageValue = componentStages[stageCode];
                  return stageValue !== undefined && stageValue !== null && !isNaN(stageValue); // Accept all values, including negatives
                });
                
                if (hasDataInGroup) {
                  const component = lcaData.components.find(c => c.id === componentId);
                  if (component) {
                    relatedComponents.add(component.name);
                  }
                }
              });
            }
            
            const impactInfo = lcaData.impactCategories.find(c => c.id === selectedImpact);
            
            const stageDescriptions = {
              'A1-A3': 'Raw Material Extraction, Manufacturing, Transport to Site',
              'A4-A5': 'Transport to Site, Construction Installation',
              'B1-B7': 'Use Stage (Maintenance, Repair, Replacement, Energy, Water)',
              'C1-C4': 'End-of-Life (Demolition, Transport, Processing, Disposal)',
              'D': 'Benefits and Loads Beyond System Boundary'
            };
            
            tooltipContent = `
                <div style="font-weight: bold; color: #000000; margin-bottom: 8px; font-size: 14px;">${d.name}</div>
                <div style="margin-bottom: 8px; color: #000000; font-weight: 600;">${d.description}</div>
                <div style="margin: 8px 0; color: #000000;">
                  <div style="margin-bottom: 4px;"><span style="font-weight: 600;">Total Impact:</span> ${totalImpact.toLocaleString()} ${impactInfo?.unit || 'kg CO₂-eq'}</div>
                  <div style="margin-bottom: 4px;"><span style="font-weight: 600;">Components:</span> ${relatedComponents.size}</div>
                  <div style="margin-bottom: 4px;"><span style="font-weight: 600;">Stages:</span> ${stageCodes.join(', ')}</div>
                </div>
                <div style="color: #666666; font-size: 11px; margin-top: 6px; padding-top: 6px; border-top: 1px solid #e5e7eb; line-height: 1.3;">
                  ${stageDescriptions[stageGroup] || 'Life cycle assessment phase'}
                </div>
            `;
          }

          tooltip.html(tooltipContent);
          
          // Position tooltip with optimized calculation
          const tooltipNode = tooltip.node();
          const tooltipRect = tooltipNode.getBoundingClientRect();
          const x = Math.min(event.pageX + 10, window.innerWidth - tooltipRect.width - 20);
          const y = Math.max(event.pageY - tooltipRect.height - 10, 20);
          
          tooltip
            .style('left', x + 'px')
            .style('top', y + 'px');
        })
        .on('mousemove', function(event, d) {
          // Update tooltip position with optimized calculation
          const tooltip = d3.select('.sankey-tooltip');
          if (!tooltip.empty()) {
            const tooltipNode = tooltip.node();
            const tooltipRect = tooltipNode.getBoundingClientRect();
            const x = Math.min(event.pageX + 10, window.innerWidth - tooltipRect.width - 20);
            const y = Math.max(event.pageY - tooltipRect.height - 10, 20);
            
            tooltip
              .style('left', x + 'px')
              .style('top', y + 'px');
          }
        })
        .on('mouseout', function(event, d) {
          // Clear hover effect and tooltip immediately when leaving this node
          d3.select(this)
            .classed('hovered', false)
            .attr('opacity', 1)
            .attr('filter', 'none');
          
          // Remove tooltip immediately
          d3.select('.sankey-tooltip').remove();
        })
        .on('click', function(event, d) {
          // Check if this node is already highlighted
          const isAlreadyHighlighted = d3.select(this).classed('clicked');
          
          if (isAlreadyHighlighted) {
            // If already highlighted, clear all highlights
            d3.selectAll('.sankey-tooltip').remove();
            
            d3.selectAll('.sankey-node')
              .classed('clicked', false)
              .classed('incoming', false)
              .classed('outgoing', false)
              .attr('opacity', 1)
              .attr('filter', 'none');
            
            d3.selectAll('.sankey-link')
              .classed('clicked', false)
              .classed('incoming', false)
              .classed('outgoing', false)
              .attr('opacity', 0.7)
              .attr('stroke-width', d => Math.max(2, d.width + 1))
              .attr('filter', 'none');
            
            console.log('Cleared all click highlights');
            return;
          }
          
          // Remove any existing tooltips first
          d3.selectAll('.sankey-tooltip').remove();
          
          // Clear any existing click highlights
          d3.selectAll('.sankey-node')
            .classed('clicked', false)
            .classed('incoming', false)
            .classed('outgoing', false)
            .attr('opacity', 1)
            .attr('filter', 'none');
          
          d3.selectAll('.sankey-link')
            .classed('clicked', false)
            .classed('incoming', false)
            .classed('outgoing', false)
            .attr('opacity', 0.7)
            .attr('stroke-width', d => Math.max(2, d.width + 1))
            .attr('filter', 'none');
          
          // Highlight the clicked node and bring it to front
          d3.select(this)
            .classed('clicked', true)
            .attr('opacity', 0.9)
            .attr('filter', 'drop-shadow(0 6px 12px rgba(0,0,0,0.3))')
            .raise();
          
          // Find incoming nodes (nodes that flow INTO this node)
          const incomingLinks = sankeyData.links.filter(link => link.target.id === d.id);
          const incomingNodeIds = [...new Set(incomingLinks.map(link => link.source.id))];
          
          // Find outgoing nodes (nodes that flow OUT FROM this node)
          const outgoingLinks = sankeyData.links.filter(link => link.source.id === d.id);
          const outgoingNodeIds = [...new Set(outgoingLinks.map(link => link.target.id))];
          
          // Highlight incoming nodes and bring them to front
          incomingNodeIds.forEach(nodeId => {
            d3.selectAll(`.sankey-node[data-id="${nodeId}"]`)
              .classed('incoming', true)
              .attr('opacity', 0.8)
              .attr('filter', 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))')
              .raise();
          });
          
          // Highlight outgoing nodes and bring them to front
          outgoingNodeIds.forEach(nodeId => {
            d3.selectAll(`.sankey-node[data-id="${nodeId}"]`)
              .classed('outgoing', true)
              .attr('opacity', 0.8)
              .attr('filter', 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))')
              .raise();
          });
          
          // Highlight incoming links and bring them to front
          incomingLinks.forEach(link => {
            d3.selectAll(`.sankey-link[data-source="${link.source.id}"][data-target="${link.target.id}"]`)
              .classed('incoming', true)
              .attr('opacity', 1)
              .attr('stroke-width', Math.max(3, link.width + 2))
              .attr('filter', 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))')
              .raise();
          });
          
          // Highlight outgoing links and bring them to front
          outgoingLinks.forEach(link => {
            d3.selectAll(`.sankey-link[data-source="${link.source.id}"][data-target="${link.target.id}"]`)
              .classed('outgoing', true)
              .attr('opacity', 1)
              .attr('stroke-width', Math.max(3, link.width + 2))
              .attr('filter', 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))')
              .raise();
          });
          
          // Log the connections for debugging
          console.log(`Clicked node: ${d.name} (${d.type})`);
          console.log(`Incoming nodes: ${incomingNodeIds.length}`, incomingNodeIds);
          console.log(`Outgoing nodes: ${outgoingNodeIds.length}`, outgoingNodeIds);
        });

      // Add node labels with optimized rendering
      labelGroup.selectAll('text')
        .data(nodes)
        .enter()
        .append('text')
        .attr('x', d => {
          // Position labels based on node position
          if (d.x0 < width / 3) {
            return d.x1 + 8; // Right side for left nodes
          } else if (d.x0 > width * 2/3) {
            return d.x0 - 8; // Left side for right nodes
          } else {
            return d.x0 < width / 2 ? d.x1 + 8 : d.x0 - 8; // Smart positioning
          }
        })
        .attr('y', d => (d.y1 + d.y0) / 2)
        .attr('dy', '0.35em')
        .attr('text-anchor', d => {
          // Smart text anchoring
          if (d.x0 < width / 3) return 'start';
          if (d.x0 > width * 2/3) return 'end';
          return d.x0 < width / 2 ? 'start' : 'end';
        })
        .attr('font-size', '11px')
        .attr('font-weight', '600')
        .attr('fill', '#1f2937')
        .attr('class', 'node-label')
        .text(d => {
          // Show descriptive names for different node types
          if (d.type === 'building') {
            return d.name.length > 12 ? d.name.substring(0, 12) + '...' : d.name;
          } else if (d.type === 'category') {
            return d.name.length > 10 ? d.name.substring(0, 10) + '...' : d.name;
          } else if (d.type === 'component') {
            return d.name.length > 12 ? d.name.substring(0, 12) + '...' : d.name;
          } else if (d.type === 'material') {
            return d.name.length > 8 ? d.name.substring(0, 8) + '...' : d.name;
          } else if (d.type === 'lca_stage') {
            // Use shorter names for LCA stages
            const shortNames = {
              'Raw Material Extraction': 'Extraction',
              'Manufacturing': 'Manufacturing',
              'Transport': 'Transport',
              'Construction': 'Construction',
              'Use Phase': 'Use',
              'End of Life': 'End of Life'
            };
            return shortNames[d.name] || d.name;
          }
          return d.name;
        })
        .style('pointer-events', 'none'); // Prevent label from interfering with node hover





    }, 100), // 100ms debounce for smooth performance
    [sankeyData, lcaData, selectedImpact]
  );

  // Optimized useEffect with cleanup
  useEffect(() => {
    // Clear any existing timeout
    if (renderTimeoutRef.current) {
      clearTimeout(renderTimeoutRef.current);
    }

    // Set new timeout for rendering
    renderTimeoutRef.current = setTimeout(() => {
      debouncedRender();
      requestAnimationFrame(() => updateSankeyScrollIndicator());
    }, 50);

    // Cleanup function
    return () => {
      if (renderTimeoutRef.current) {
        clearTimeout(renderTimeoutRef.current);
      }
    };
  }, [sankeyData, debouncedRender, updateSankeyScrollIndicator]);

  // Add keyboard event listener for clearing highlights
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        // Remove any existing tooltips first
        d3.selectAll('.sankey-tooltip').remove();
        
        // Clear all click highlights when Escape is pressed
        d3.selectAll('.sankey-node')
          .classed('clicked', false)
          .classed('incoming', false)
          .classed('outgoing', false)
          .attr('opacity', 1)
          .attr('filter', 'none');
        
        d3.selectAll('.sankey-link')
          .classed('clicked', false)
          .classed('incoming', false)
          .classed('outgoing', false)
          .attr('opacity', 0.7)
          .attr('stroke-width', d => Math.max(2, d.width + 1))
          .attr('filter', 'none');
        
        console.log('Cleared all highlights (Escape key)');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  if (!lcaData) {
    return (
      <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl">
        <CardHeader>
          <CardTitle className="flex items-center text-gray-800">
            <BarChart3 className="w-5 h-5 mr-2 text-blue-600" />
            Sankey Flow Diagram
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-gray-500 py-8">
            Upload an Excel file or use sample data to view the Sankey diagram
          </div>
        </CardContent>
      </Card>
    );
  }

  const selectedImpactInfo = lcaData.impactCategories.find(cat => cat.id === selectedImpact);

  return (
    <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl">
      <CardHeader>
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-col items-center gap-4 md:flex-row md:items-center md:gap-3">
            <CardTitle className="flex items-center justify-center text-center text-gray-800 md:justify-start md:text-left">
              <GitBranch className="w-5 h-5 mr-2 text-emerald-600" />
              Sankey Flow Diagram
            </CardTitle>

            <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row md:flex-nowrap md:items-center md:justify-start md:space-x-3 md:gap-0">
              <div className="w-full md:w-auto">
                {/* Edit Chart Components Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full flex-wrap gap-1 whitespace-normal text-xs leading-tight md:w-auto md:text-sm"
                    >
                      <Settings className="w-4 h-4" />
                      <span className="whitespace-normal">Edit Diagram Components</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>Chart Columns</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuCheckboxItem
                      checked={visibleColumns.building}
                      onCheckedChange={(checked) => 
                        setVisibleColumns(prev => ({ ...prev, building: checked }))
                      }
                    >
                      Building
                    </DropdownMenuCheckboxItem>
                    <DropdownMenuCheckboxItem
                      checked={visibleColumns.categories}
                      onCheckedChange={(checked) => 
                        setVisibleColumns(prev => ({ ...prev, categories: checked }))
                      }
                    >
                      Categories
                    </DropdownMenuCheckboxItem>
                    <DropdownMenuCheckboxItem
                      checked={visibleColumns.components}
                      onCheckedChange={(checked) => 
                        setVisibleColumns(prev => ({ ...prev, components: checked }))
                      }
                    >
                      Components
                    </DropdownMenuCheckboxItem>
                    {(() => {
                      if (hasActualMaterialData) {
                        return (
                          <DropdownMenuCheckboxItem
                            checked={visibleColumns.materials}
                            onCheckedChange={(checked) => 
                              setVisibleColumns(prev => ({ ...prev, materials: checked }))
                            }
                          >
                            Materials {isSampleData ? '(Sample Data)' : ''}
                          </DropdownMenuCheckboxItem>
                        );
                      } else {
                        return (
                          <DropdownMenuCheckboxItem disabled>
                            Materials (No data)
                          </DropdownMenuCheckboxItem>
                        );
                      }
                    })()}
                    {(() => {
                      if (hasActualLCAStageData) {
                        return (
                          <DropdownMenuCheckboxItem
                            checked={visibleColumns.lcaStages}
                            onCheckedChange={(checked) => 
                              setVisibleColumns(prev => ({ ...prev, lcaStages: checked }))
                            }
                          >
                            LCA Stages {isSampleData ? '(Sample Data)' : ''}
                          </DropdownMenuCheckboxItem>
                        );
                      } else {
                        return (
                          <DropdownMenuCheckboxItem disabled>
                            LCA Stages (No data)
                          </DropdownMenuCheckboxItem>
                        );
                      }
                    })()}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              
              <div className="grid w-full grid-cols-2 gap-2 md:w-auto md:flex md:flex-row md:space-x-3 md:gap-0">
                {/* Aggregate Results Toggle - Only for Excel upload */}
                {!isSampleData && (
                  <div className="w-full md:w-auto">
                    <Button
                      variant={aggregateResults ? "default" : "outline"}
                      size="sm"
                      onClick={() => setAggregateResults(!aggregateResults)}
                      className={`w-full md:w-auto ${aggregateResults ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}`}
                    >
                      <BarChart3 className="w-4 h-4" />
                      {aggregateResults ? "Aggregated" : "Aggregate Results"}
                    </Button>
                  </div>
                )}

                {/* Download Diagram Button */}
                <div className="w-full md:w-auto">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={downloadDiagramAsPDF}
                    disabled={isDownloading}
                    className="w-full md:w-auto border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isDownloading ? (
                      <div className="w-3 h-3 mr-2 border-2 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    {isDownloading ? 'Generating...' : 'Download Diagram'}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center md:justify-end">
            <Badge variant="outline" className="border-gray-200 text-gray-700">
              Flow: {(() => {
                const flow = [];
                if (visibleColumns.building) flow.push('Building');
                if (visibleColumns.categories) flow.push('Categories');
                if (visibleColumns.components) flow.push('Components');
                if (visibleColumns.materials && hasActualMaterialData) flow.push('Materials');
                if (visibleColumns.lcaStages && hasActualLCAStageData) flow.push('LCA Stages');
                return flow.join(' → ');
              })()}
            </Badge>
          </div>
        </div>
      </CardHeader>
      
      {/* Instruction text centered below header */}
      <div className="px-4 text-center text-xs text-gray-500 py-3 border-b border-gray-100 md:px-0 md:text-sm">
        Click a node to highlight its connections, and hover over it to view details. Click the same node again to clear the highlights. You can also hover over links to see their names. Use the ‘Aggregate Results’ toggle to group links with the same inflow and outflow, instead of displaying them individually.
      </div>
      
      <CardContent>
        <div className="space-y-4">
          <div className="relative">
            <div
              ref={sankeyScrollContainerRef}
              className="overflow-x-auto md:overflow-visible hide-scrollbar"
              data-mobile-scroll
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              <div className="min-w-[960px] md:min-w-0 md:w-full">
                <div className="relative mx-auto md:max-w-max md:justify-center md:flex">
                  <svg ref={svgRef} className="rounded-lg bg-gray-50" style={{ width: '1200px', height: '800px' }}></svg>
                  
                  {/* PDF Generation Overlay */}
                  {isDownloading && (
                    <div className="absolute inset-0 bg-white/95 backdrop-blur-md z-40 flex flex-col items-center justify-center rounded-lg border-2 border-emerald-200 shadow-xl">
                      {/* Industrial Carbon Process Visualization */}
                      <div className="relative mb-8">
                        {/* Main Industrial Container */}
                        <div className="w-24 h-20 bg-gradient-to-b from-emerald-100 to-emerald-200 rounded-lg border-2 border-emerald-300 shadow-lg relative overflow-hidden">
                          {/* Carbon Emission Flow Lines */}
                          <div className="absolute inset-0">
                            {/* Horizontal flow lines */}
                            <div className="absolute top-2 left-0 w-full h-0.5 bg-emerald-400 animate-pulse"></div>
                            <div className="absolute top-4 left-0 w-full h-0.5 bg-emerald-400 animate-pulse" style={{animationDelay: '0.3s'}}></div>
                            <div className="absolute top-6 left-0 w-full h-0.5 bg-emerald-400 animate-pulse" style={{animationDelay: '0.6s'}}></div>
                            <div className="absolute top-8 left-0 w-full h-0.5 bg-emerald-400 animate-pulse" style={{animationDelay: '0.9s'}}></div>
                            <div className="absolute top-10 left-0 w-full h-0.5 bg-emerald-400 animate-pulse" style={{animationDelay: '1.2s'}}></div>
                          </div>
                          
                          {/* Vertical processing columns */}
                          <div className="absolute left-2 top-1 w-1 h-16 bg-emerald-500 rounded-full animate-pulse"></div>
                          <div className="absolute left-6 top-1 w-1 h-16 bg-emerald-500 rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
                          <div className="absolute left-10 top-1 w-1 h-16 bg-emerald-500 rounded-full animate-pulse" style={{animationDelay: '0.8s'}}></div>
                          <div className="absolute left-14 top-1 w-1 h-16 bg-emerald-500 rounded-full animate-pulse" style={{animationDelay: '1.2s'}}></div>
                          <div className="absolute left-18 top-1 w-1 h-16 bg-emerald-500 rounded-full animate-pulse" style={{animationDelay: '1.6s'}}></div>
                          <div className="absolute left-22 top-1 w-1 h-16 bg-emerald-500 rounded-full animate-pulse" style={{animationDelay: '2s'}}></div>
                          
                          {/* Carbon particles flowing through */}
                          <div className="absolute top-1 left-1 w-1 h-1 bg-emerald-600 rounded-full animate-bounce" style={{animationDelay: '0s'}}></div>
                          <div className="absolute top-3 left-5 w-1 h-1 bg-emerald-600 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                          <div className="absolute top-5 left-9 w-1 h-1 bg-emerald-600 rounded-full animate-bounce" style={{animationDelay: '0.4s'}}></div>
                          <div className="absolute top-7 left-13 w-1 h-1 bg-emerald-600 rounded-full animate-bounce" style={{animationDelay: '0.6s'}}></div>
                          <div className="absolute top-9 left-17 w-1 h-1 bg-emerald-600 rounded-full animate-bounce" style={{animationDelay: '0.8s'}}></div>
                          <div className="absolute top-11 left-21 w-1 h-1 bg-emerald-600 rounded-full animate-bounce" style={{animationDelay: '1s'}}></div>
                        </div>
                        
                        {/* Industrial Pipes and Connections */}
                        <div className="absolute -top-2 left-4 w-16 h-1 bg-emerald-400 rounded-full animate-pulse"></div>
                        <div className="absolute -top-2 right-4 w-16 h-1 bg-emerald-400 rounded-full animate-pulse" style={{animationDelay: '0.5s'}}></div>
                        <div className="absolute -bottom-2 left-4 w-16 h-1 bg-emerald-400 rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
                        <div className="absolute -bottom-2 right-4 w-16 h-1 bg-emerald-400 rounded-full animate-pulse" style={{animationDelay: '1.5s'}}></div>
                        
                        {/* Connection Nodes */}
                        <div className="absolute -top-2 left-4 w-2 h-2 bg-emerald-600 rounded-full animate-ping"></div>
                        <div className="absolute -top-2 right-4 w-2 h-2 bg-emerald-600 rounded-full animate-ping" style={{animationDelay: '0.5s'}}></div>
                        <div className="absolute -bottom-2 left-4 w-2 h-2 bg-emerald-600 rounded-full animate-ping" style={{animationDelay: '1s'}}></div>
                        <div className="absolute -bottom-2 right-4 w-2 h-2 bg-emerald-600 rounded-full animate-ping" style={{animationDelay: '1.5s'}}></div>
                        
                        {/* Carbon Emission Clouds */}
                        <div className="absolute -top-4 left-8 w-3 h-2 bg-emerald-300 rounded-full opacity-60 animate-pulse"></div>
                        <div className="absolute -top-4 right-8 w-3 h-2 bg-emerald-300 rounded-full opacity-60 animate-pulse" style={{animationDelay: '0.7s'}}></div>
                        <div className="absolute -bottom-4 left-8 w-3 h-2 bg-emerald-300 rounded-full opacity-60 animate-pulse" style={{animationDelay: '1.4s'}}></div>
                        <div className="absolute -bottom-4 right-8 w-3 h-2 bg-emerald-300 rounded-full opacity-60 animate-pulse" style={{animationDelay: '2.1s'}}></div>
                      </div>
                      
                      {/* Industrial Process Text */}
                      <div className="text-xl font-semibold text-emerald-700 mb-3 animate-pulse">
                        Processing Sankey Diagram
                      </div>
                      
                      {/* Process Status Indicators */}
                      <div className="flex space-x-3 mb-4">
                        <div className="flex flex-col items-center">
                          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-ping"></div>
                          <div className="text-xs text-emerald-600 mt-1">Collecting</div>
                        </div>
                        <div className="flex flex-col items-center">
                          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-ping" style={{animationDelay: '0.3s'}}></div>
                          <div className="text-xs text-emerald-600 mt-1">Analyzing</div>
                        </div>
                        <div className="flex flex-col items-center">
                          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-ping" style={{animationDelay: '0.6s'}}></div>
                          <div className="text-xs text-emerald-600 mt-1">Generating</div>
                        </div>
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="w-48 h-2 bg-emerald-100 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500 rounded-full animate-pulse" style={{width: '100%'}}></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <ScrollIndicator indicator={sankeyScrollIndicator} className="mt-3 h-1" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SankeyDiagram;
